import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { interpolate } from '../lib/interpolate';
import { getLangFromPath, replaceLangInPath } from '@/lib/i18nRouting';
import { setAnalyticsLanguage, trackEvent } from '@/lib/analytics';

declare global {
  var __PRERENDER_LANGUAGE__: string | undefined;
}

export type Language = 'es' | 'en' | 'pt';

const supportedLanguages: Language[] = ['es', 'en', 'pt'];
const languageStorageKey = 'language';

const normalizeLanguage = (value: string | null | undefined): Language | null => {
  if (!value) {
    return null;
  }

  const normalized = value.toLowerCase();
  const base = normalized.split('-')[0] as Language;
  return supportedLanguages.includes(base) ? base : null;
};

const getPathLanguage = (): Language | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  if (!/^\/(es|en|pt)(\/|$)/.test(window.location.pathname)) {
    return null;
  }
  const pathLanguage = getLangFromPath(window.location.pathname);
  return supportedLanguages.includes(pathLanguage as Language)
    ? (pathLanguage as Language)
    : null;
};

const getServerLanguage = (): Language | null => {
  if (typeof window !== 'undefined') {
    return null;
  }
  return normalizeLanguage(globalThis.__PRERENDER_LANGUAGE__);
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, unknown>) => string;
}


export const translations = {
  es: {
    header: {
      title: 'UnFollowed',
      subtitle: 'Analiza tus seguidores y seguidos',
      login: 'Iniciar Sesión',
      logout: 'Cerrar Sesión',
      settings: 'Configuración',
      theme: {
        light: 'Claro',
        dark: 'Oscuro',
        system: 'Sistema'
      }
    },
    welcome: {
      title: 'Analiza tus Seguidores de Instagram',
      subtitle: 'Descubre quién no te sigue de vuelta y quién sí',
      description: 'Sube tus archivos de datos de Instagram para obtener un análisis detallado de tus seguidores y seguidos.',
      followSteps: 'Sigue estos pasos para obtener tus datos de Instagram'
      },
    landing: {
      hero: {
        title: 'Descubre en forma segura quién dejó de seguirte en Instagram',
        titleLeading: 'Descubre en forma segura quién',
        titleAccent: 'dejó de seguirte en Instagram',
        subtitle: 'Convertí los datos de tus seguidores de Instagram en insights accionables, en minutos.',
        primaryCta: 'Iniciar sesión para comenzar',
        howItWorksCta: 'Cómo funciona',
        securityChip: 'Nunca ingresás tu contraseña de Instagram en nuestra aplicación; todo se hace desde la web oficial de Instagram.'
      },
      seo: {
        title: 'UnFollowed | Cómo ver quién me dejó de seguir en Instagram',
        description: 'UnFollowed te muestra cómo ver quién dejó de seguirte en Instagram con un análisis seguro basado en el ZIP oficial de Instagram.',
        keywords: 'UnFollowed, Instagram, seguidores, quien dejo de seguirme, como ver quien me dejo de seguir en instagram, unfollowers'
      },
      steps: {
        title: 'Cómo funciona'
      },
      howItWorks: {
        demoCta: 'Ver demo interactiva',
        downloadButton: 'Download data',
        downloadingLabel: 'Downloading...',
        downloadComplete: 'Download complete',
        downloadAgain: 'Download again',
        dropLabel: 'Soltá tu archivo aquí',
        uploadUploaded: 'Archivo subido ✓',
        uploadProcessing: 'Procesando...',
        uploadReady: 'Listo',
        play: 'Reproducir',
        pause: 'Pausar',
        steps: {
          download: {
            title: 'Descargá tu información',
            subtitle: '',
            description: 'Solicitá la descarga de tus datos desde Instagram para acceder a tu información.'
          },
          upload: {
            title: 'Subí tu información',
            subtitle: '',
            description: 'Arrastrá y soltá el archivo ZIP descargado desde Instagram para analizarlo.'
          },
          results: {
            title: 'Ver resultados al instante',
            subtitle: '',
            description: 'Identificá quien dejo de seguirte en Instagram y mirá tus métricas de forma simple.'
          }
        },
        resultsTabs: {
          notFollowing: 'No sigues',
          notFollowed: 'No te siguen'
        },
        uploadSuccess: 'Archivos subidos con éxito',
        actions: {
          copyUser: 'Copiar usuario',
          openProfile: 'Ver perfil'
        }
      },
      trust: {
        title: 'Privacidad primero',
        privacy: 'Tus archivos no se comparten.',
        privacyDescription: 'Solo tú puedes ver tus archivos y eliminarlos en cualquier momento.',
        highlight1: 'Encriptado extremo a extremo',
        highlight2: 'Compatibilidad garantizada con Instagram'
      },
      features: {
        title: 'Características principales',
        cards: {
          notFollowingBack: {
            title: 'Usuarios perdidos',
            description: 'Lista completa de usuarios que sigues pero no te siguen'
          },
          newFollowers: {
            title: 'Nuevos seguidores',
            description: 'Descubre quién empezó a seguirte recientemente'
          },
          stats: {
            title: 'Estadísticas detalladas',
            description: 'Gráficos y métricas de crecimiento'
          },
          timeline: {
            title: 'Historial temporal',
            description: 'Visualiza los cambios de tus seguidores en el tiempo'
          }
        }
      },
      faq: {
        title: 'Preguntas frecuentes',
        items: {
          safeUnfollowed: {
            question: '🔍 ¿Cómo ver quién dejó de seguirme en Instagram gratis y de forma segura?',
            answer:
              'Con UnFollowed podés ver quién dejó de seguirte en Instagram gratis y de manera segura.\nSolo tenés que descargar tu información de Instagram en un archivo ZIP desde la configuración de tu cuenta y subirlo a la app. UnFollowed analiza tus seguidores y seguidos para mostrarte quién dejó de seguirte, sin iniciar sesión en Instagram, sin compartir tu contraseña y sin usar aplicaciones de pago.'
          },
          accountSafety: {
            question: '🛡️ ¿Usar esta app puede hacer que Instagram bloquee o suspenda mi cuenta?',
            answer:
              'No. Esta app es 100% segura y no viola las reglas de Instagram.\nA diferencia de otras aplicaciones similares:\n• No pedimos tu usuario ni contraseña\n• No accedemos directamente a tu cuenta\n• No realizamos scraping\n• No ejecutamos acciones automáticas\nEl análisis se realiza únicamente a partir de los datos oficiales que Instagram te permite descargar, manteniendo tu cuenta totalmente segura frente a bloqueos o suspensiones.'
          },
          accuracy: {
            question: '🎯 ¿Qué tan preciso es el análisis?',
            answer:
              'El análisis es muy preciso porque se basa en los datos oficiales exportados por Instagram.\nCuantas más veces subas archivos actualizados, mayor información vas a tener sobre tus métricas en el tiempo, incluyendo los cambios con respecto al último análisis subido.'
          },
          autoTracking: {
            question: '🤖 ¿La app hace el seguimiento automáticamente?',
            answer:
              'No, y eso es intencional. Para mantener tu cuenta segura y evitar cualquier riesgo, la app no se conecta automáticamente a Instagram.\nCada vez que quieras un análisis actualizado, simplemente subís el nuevo archivo ZIP que te entrega Instagram.'
          },
          whyDownload: {
            question: '📦 ¿Por qué tengo que descargar el archivo desde Instagram?',
            answer:
              'Instagram no permite que aplicaciones externas accedan directamente a tu información privada.\nDescargar el archivo ZIP es la forma oficial y segura de obtener tus datos y mantener el control total sobre tu cuenta.'
          },
          generationTime: {
            question: '⏱️ ¿Cuánto demora Instagram en generar el archivo?',
            answer:
              'Normalmente entre unos pocos minutos y hasta una hora, dependiendo de la cantidad de información de tu cuenta.\nCuando el archivo esté listo, Instagram te avisa por email.'
          },
          techKnowledge: {
            question: '🙋‍♀️ ¿Necesito conocimientos técnicos para usar la app?',
            answer:
              'No, para nada. Es súper fácil: descargás el ZIP desde Instagram, lo subís y listo.\nNosotros nos encargamos del resto.'
          },
          unfollowNotifications: {
            question: '🔔 ¿Instagram avisa cuando alguien deja de seguirte?',
            answer:
              'No. Instagram no envía notificaciones cuando alguien deja de seguirte.\nPor eso, UnFollowed es una forma simple y segura de saber quién dejó de seguirte sin tener que revisar manualmente en Instagram entre tus seguidos y seguidores.'
          },
          blockedUsers: {
            question: '🚫 ¿UnFollowed puede mostrar quién te bloqueó en Instagram?',
            answer:
              'UnFollowed está diseñado para detectar quién dejó de seguirte a partir de los datos oficiales que descargás desde Instagram.\nSi alguien te bloquea, Instagram deja de mostrarte su perfil, publicaciones e historias. Sin embargo, Instagram no incluye información sobre bloqueos en los datos descargables, por lo que ninguna app (incluida UnFollowed) puede mostrar una lista exacta de quién te bloqueó.\nLo que sí permite UnFollowed es identificar cambios en tus seguidores y detectar quién dejó de seguirte, lo que en algunos casos puede coincidir con un bloqueo.'
          }
        }
      },
      benefits: {
        items: {
          secure: {
            title: '100% seguro',
            subtitle: 'Tus datos, tu control.'
          },
          unfollowed: {
            title: 'Descubrí quién te dejó de seguir',
            subtitle: 'Identificá de forma simple y clara los usuarios que dejaron de seguirte.'
          },
          noAccess: {
            title: 'No requiere acceso a Instagram',
            subtitle: 'Nunca compartís tu contraseña con esta app.'
          },
          free: {
            title: '100% gratis',
            subtitle: 'Usá todas las funcionalidades sin costo.'
          }
        }
      },
      cta: {
        title: '¿Listo para descubrir tus insights?',
        description: 'Comienza ahora y obtén análisis detallados de tus seguidores en minutos.',
        button: 'Comenzar ahora'
      },
      footer: {
        copyright: '© 2026 UnFollowed. Todos los derechos reservados.',
        disclaimer: 'No estamos afiliados con Instagram o Meta.'
      },
      footerLinks: {
        navigation: 'Navegación',
        home: 'Inicio',
        howItWorks: 'Cómo funciona',
        faq: 'FAQ',
        legal: 'Legal',
        terms: 'Términos y condiciones',
        privacy: 'Política de privacidad',
        contactTitle: '¿Tenés preguntas?',
        contactCta: 'Contactanos'
      }
    },
    termsPage: {
      title: 'Términos y condiciones de UnFollowed',
      intro: 'Leé estos Términos y condiciones detenidamente antes de usar nuestro servicio.',
      effectiveDate: 'Fecha de vigencia: 01/01/2026',
      sections: {
        introduction: {
          title: '1. Introducción',
          paragraph1:
            'UnFollowed ofrece una herramienta que permite a los usuarios analizar sus seguidores y quienes dejaron de seguirlos en Instagram utilizando únicamente los datos oficiales que el usuario descarga desde Instagram.',
          paragraph2: 'Al acceder o utilizar UnFollowed, aceptás estos Términos y condiciones.'
        },
        use: {
          title: '2. Uso del servicio',
          paragraph1:
            'UnFollowed permite subir archivos de datos de Instagram obtenidos directamente desde Instagram para identificar unfollowers y cambios de seguidores a lo largo del tiempo.',
          bullets: {
            noPassword: 'No requerimos tu usuario ni contraseña de Instagram.',
            noDirectAccess: 'No accedemos a cuentas de Instagram directamente.',
            noAutomation: 'No realizamos scraping, automatizaciones ni acciones en nombre de los usuarios.',
            userProvided: 'El análisis se realiza exclusivamente con los datos que el usuario decide proporcionar.'
          }
        },
        privacy: {
          title: '3. Privacidad',
          paragraph1: 'Respetamos tu privacidad y estamos comprometidos con la protección de tus datos.',
          paragraph2:
            'Consultá nuestra Política de privacidad para más información sobre cómo recopilamos, procesamos, almacenamos y protegemos tu información.'
        },
        responsible: {
          title: '4. Uso responsable',
          paragraph1:
            'Los usuarios se comprometen a usar UnFollowed de manera responsable y cumpliendo todas las leyes y normas aplicables.',
          paragraph2:
            'No podés usar el servicio con fines ilegales, dañinos, abusivos, difamatorios o invasivos, incluyendo acoso o violación de la privacidad de terceros.',
          paragraph3:
            'UnFollowed no es responsable de cómo los usuarios interpretan o usan la información del servicio. Cada usuario es responsable por sus acciones y sus consecuencias.'
        },
        limitations: {
          title: '5. Limitaciones del servicio',
          paragraph1:
            'UnFollowed brinda insights basados en los datos que Instagram pone a disposición mediante sus herramientas de exportación.',
          bullets: {
            noGuarantees: 'No garantizamos exactitud absoluta, integridad ni disponibilidad ininterrumpida del servicio.',
            resultsMayVary:
              'Los resultados pueden variar según la calidad, la completitud y el momento de los datos subidos.',
            instagramChanges:
              'Cambios en la estructura de datos o políticas de exportación de Instagram pueden afectar la funcionalidad o la precisión del servicio.'
          },
          paragraph2: 'Los usuarios reconocen y aceptan estas limitaciones.'
        },
        changes: {
          title: '6. Cambios en los términos',
          paragraph1: 'Podemos actualizar o modificar estos Términos y condiciones en cualquier momento.',
          paragraph2:
            'Cualquier cambio se publicará en esta página y entrará en vigencia inmediatamente al publicarse.',
          paragraph3:
            'Si seguís usando UnFollowed luego de los cambios, aceptás los nuevos Términos y condiciones.'
        }
      }
    },
    privacyPage: {
      title: 'Política de privacidad de UnFollowed',
      intro:
        'Tu privacidad es importante para nosotros. Esta Política de privacidad explica cómo UnFollowed recopila, usa y protege tu información.',
      effectiveDate: 'Fecha de vigencia: 01/01/2026',
      sections: {
        introduction: {
          title: '1. Introducción',
          paragraph1:
            'UnFollowed es una herramienta que permite analizar datos de seguidores de Instagram a partir de archivos descargados directamente desde Instagram y subidos voluntariamente por el usuario.',
          paragraph2:
            'Al usar UnFollowed, aceptás la recopilación y el uso de la información de acuerdo con esta Política de privacidad.'
        },
        collect: {
          title: '2. Información que recopilamos',
          paragraph1: 'Recopilamos únicamente los datos mínimos necesarios para brindar el servicio.',
          subheadingProvided: 'a) Información que proporcionás',
          bullets: {
            instagramFiles: 'Archivos de datos de Instagram (como archivos JSON) que descargaste desde Instagram.',
            technicalMetadata:
              'Metadatos técnicos necesarios para procesar el análisis (por ejemplo, estructura de archivos o marcas de tiempo).'
          },
          subheadingNotRequested: 'No solicitamos:',
          notBullets: {
            noUsername: 'Usuario o contraseña de Instagram.',
            noDirectAccess: 'Acceso directo a tu cuenta de Instagram.'
          }
        },
        use: {
          title: '3. Cómo usamos tu información',
          paragraph1: 'La información que proporcionás se utiliza exclusivamente para:',
          bullets: {
            analyze: 'Analizar seguidores y unfollowers.',
            generate: 'Generar insights y visualizaciones dentro de la app.',
            improve: 'Mejorar el rendimiento y la confiabilidad del servicio.'
          },
          paragraph2: 'No usamos tus datos para publicidad, perfilado ni reventa.'
        },
        processing: {
          title: '4. Procesamiento y almacenamiento de datos',
          paragraph1: 'Todos los datos se procesan únicamente para entregar el análisis solicitado.',
          paragraph2: 'Los archivos subidos no se comparten con terceros.',
          paragraph3: 'Los usuarios pueden eliminar sus datos subidos en cualquier momento.',
          paragraph4:
            'Tomamos medidas técnicas y organizativas razonables para proteger tu información contra accesos no autorizados, pérdida o uso indebido.'
        },
        thirdParty: {
          title: '5. Servicios de terceros',
          paragraph1:
            'UnFollowed no se integra con la API de Instagram y no realiza scraping ni automatizaciones.',
          paragraph2:
            'Podemos usar proveedores de infraestructura confiables (como hosting o analítica) únicamente para operar y mejorar el servicio. Estos proveedores están sujetos a obligaciones de confidencialidad y protección de datos.'
        },
        cookies: {
          title: '6. Cookies y analítica',
          paragraph1:
            'Podemos usar analítica básica y cookies para entender cómo los usuarios interactúan con la app (por ejemplo, vistas de página o duración de sesión).',
          bullets: {
            anonymized: 'Se anonimizan.',
            noFiles: 'No incluyen archivos de Instagram subidos.',
            improveExperience: 'Se usan solo para mejorar la experiencia y el rendimiento.'
          },
          paragraph2: 'Podés desactivar las cookies desde la configuración de tu navegador.'
        },
        retention: {
          title: '7. Retención de datos',
          paragraph1:
            'Conservamos los datos solo el tiempo necesario para brindar el servicio o hasta que el usuario los elimine.',
          paragraph2: 'Una vez eliminados, se borran permanentemente dentro de un plazo razonable.'
        },
        rights: {
          title: '8. Tus derechos',
          paragraph1: 'Según tu jurisdicción, podés tener derecho a:',
          bullets: {
            access: 'Acceder a tus datos.',
            delete: 'Solicitar la eliminación de tus datos.',
            restrict: 'Restringir u objetar el tratamiento.'
          },
          paragraph2: 'Podés ejercer estos derechos contactándonos.'
        },
        changes: {
          title: '9. Cambios a esta Política de privacidad',
          paragraph1: 'Podemos actualizar esta Política de privacidad periódicamente.',
          paragraph2: 'Los cambios se publicarán en esta página y entrarán en vigencia al publicarse.',
          paragraph3:
            'El uso continuado de UnFollowed luego de los cambios implica la aceptación de la política actualizada.'
        },
        contact: {
          title: '10. Contacto',
          paragraph1:
            'Si tenés preguntas sobre esta Política de privacidad o sobre cómo se manejan tus datos, contactanos a través de los canales del sitio.'
        }
      }
    },
    demo: {
      badge: 'Demo interactiva',
      description: 'Estás viendo un ejemplo como si hubieras subido tus archivos y ya tuvieras al menos un análisis previo para comparar.',
      netChange: 'Cálculo neto: +5 seguidores nuevos - 2 perdidos = +3 seguidores.',
      uploadTitle: 'Generando demo interactiva',
      uploadDescription: 'Estamos preparando automáticamente los resultados de ejemplo. Esto tomará solo unos segundos.',
      cancel: 'Cancelar demo'
    },
    upload: {
      title: 'Subir y analizar archivos',
      analyzing: 'Analizando...',
      analyze: 'Analizar Datos',
      processing: 'Procesando análisis...',
      unrecognizedFile: 'Archivo no reconocido. Subí el ZIP descargado desde Instagram.',
      secureBadge: '100% seguro',
      removeFileGeneric: 'Eliminar archivo',
      processingFiles: 'Procesando archivos...',
      readingZip: 'Leyendo ZIP...',
      validatingFiles: 'Validando archivos...',
      zipMissingData: 'No encontramos estos archivos en el ZIP: {files}. Volvé a solicitar la descarga en Instagram e intentá nuevamente.',
      dropHere: 'Soltá el ZIP aquí',
      introTitle: 'Último paso para generar tu análisis',
      introDescription: 'Subí el archivo ZIP que Instagram te envió por email.',
      requiredTitle: 'ZIP requerido',
      requiredHint: 'Es el archivo ZIP que Instagram te envió por email.',
      statusUploaded: 'Subido',
      statusPartial: 'Incompleto',
      statusMissing: 'Falta',
      zipLabel: 'ZIP de Instagram',
      zipDescription: '',
      instructionsHint: '¿Todavía no descargaste el ZIP?',
      instructionsLink: 'Ver instrucciones',
      dropTitle: 'Arrasta el archivo ZIP aqui',
      dropDescription: 'O tocá para elegirlo desde tu dispositivo.',
      dropHint: 'Un solo ZIP es suficiente.',
      selectFiles: 'Seleccionar ZIP'
    },
    instructions: {
      viewInstructions: 'Ver instrucciones completas',
      wizardTitle: 'Prepará tus datos en 4 pasos',
      wizardSubtitle: '',
      progressLabel: 'Paso {current} de {total}',
      back: 'Volver',
      next: 'Continuar',
      goToUpload: 'Ir a Subir archivos',
      jsonDefinition: 'Instagram genera un ZIP con tu información. Subilo aquí y nos encargamos del resto.',
      privacyNote: 'Solo tú puedes ver tus archivos.',
      exportLink: 'https://accountscenter.instagram.com/info_and_permissions/dyi/',
      exportPopupBlocked: 'No pudimos abrir Instagram. Permití los pop-ups y volvé a intentarlo.',
      steps: {
        overview: {
          title: 'Lo que hace UnFollowed',
          description: 'Transformamos los datos oficiales de Instagram en insights claros para que tomes decisiones rápidas.',
          point1: 'Detectá quién dejó de seguirte y accedé a estadísticas clave.',
          point2: 'Identificá seguidores nuevos y compará periodos en segundos.',
          point3: 'Analizá todo desde tu navegador sin instalar nada.',
          reassurance: 'Tus datos, tu control: podés eliminarlos cuando quieras.'
        },
        files: {
          title: 'Archivos necesarios para el proceso',
          description: 'Instagram te permite descargar tus datos en un archivo ZIP.',
          jsonNote: 'Ese ZIP contiene la información necesaria para analizar:',
          followersFile: '',
          followingFile: '',
          point1: 'Quiénes te siguen',
          point2: 'A quiénes seguís',
          point3: 'Quiénes no te siguen de vuelta',
          point4: 'Y muchos otros datos relacionados a tu cuenta',
          point5: 'Todos los datos son generados directamente desde Instagram.',
          point6: 'Seguro y sin compartir tu contraseña ni poner en riesgo tu cuenta.'
        },
        download: {
          title: 'Solicitá la descarga del archivo ZIP en Instagram',
          description: 'Durante el proceso, la contraseña se ingresa en Instagram, no en nuestra app.',
          importantTitle: '⚠️ IMPORTANTE',
          importantLine1: 'Seguí estos pasos con atención.',
          importantLine2: 'Son clave para que podamos procesar toda la información correctamente y generar el análisis sin errores.',
          waitNote: '⏳ Este proceso puede demorar desde unos pocos minutos hasta aproximadamente una hora, dependiendo de la cantidad de información de tu cuenta.',
          preStepsNote: 'Los siguientes pasos se deben realizar dentro de Instagram / Centro de cuentas de Meta.',
          ctaTitle: '¿Listo para comenzar?',
          ctaDescription:
            'Haz clic en el botón para copiar el enlace del Accounts Center de Instagram y luego ábrelo en una nueva pestaña.',
          step1: 'Tocá el botón “Create export”.',
          step2: 'Elegí tu cuenta de Instagram en el Centro de cuentas.',
          step3: 'En “Información a descargar”, dejá únicamente “Connections”.',
          step4: 'Seleccioná “All time”, elegí el formato recomendado y “Media quality: Low”, y confirmá con “Create files”.',
          tipConnections: 'Información: Connections (desmarca lo demás).',
          tipFormat: 'Formato recomendado.',
          tipDateRange: 'Rango: All time.',
          tipQuality: 'Media quality: Low.',
          linkLabel: 'Copiar link',
          linkCopiedLabel: 'Link copiado'
        },
        after: {
          title: 'Volvé a UnFollowed con el ZIP listo',
          description: '',
          point1: 'Descargá el ZIP del email.',
          point2: 'Subí el ZIP y generamos tu análisis automáticamente.',
          point4: '',
          reminder: 'Podés repetir estos pasos cuando quieras para actualizar tus métricas.'
        }
      }
    },
    loading: {
      title: 'Cargando historial completo',
      subtitle: 'Preparando todos tus análisis anteriores...',
      genericTitle: 'Cargando...',
      genericSubtitle: 'Preparando tu sesión...'
    },
    results: {
      title: 'Resultados del Análisis',
      followers: 'Seguidores',
      following: 'Seguidos',
      notReciprocal: 'No te siguen de vuelta',
      newFollowers: 'Nuevos seguidores',
      downloadCSV: 'Descargar CSV',
      goBack: 'Volver',
      showHistorical: 'Ver Historial',
      search: 'Buscar usuarios...',
      copyAll: 'Copiar Todo',
      newAnalysis: 'Realizar Nuevo Análisis',
      backAction: 'Volver',
      helpAction: 'FAQ / Ayuda',
      confirmBackTitle: '¿Deseas volver ?',
      confirmBackDescription: 'Vas a salir del análisis actual. Podrás volver a verlo más tarde desde el historial.',
      confirmBackConfirm: 'Volver',
      confirmBackCancel: 'Cancelar',
      importantNoticeTitle: 'Avisos importantes',
      importantNoticeBody:
        'Instagram puede incluir cuentas desactivadas en los datos descargados. Si al abrir un perfil ves “user not found”, esa cuenta ya no existe y no se trata de un unfollower real.',
      importantNoticeLine1: 'Instagram puede incluir cuentas desactivadas en los datos descargados.',
      importantNoticeLine2Prefix: 'Si al abrir un perfil ves',
      importantNoticeNotFound: 'user not found',
      importantNoticeLine2Middle: ', esa cuenta ya no existe y',
      importantNoticeNotUnfollower: 'no se trata de un unfollower real',
      importantNoticeLine2Suffix: '.',
      importantNoticeConfirm: 'Entendido',
      notFollowingYouTitle: 'No te siguen de vuelta',
      notFollowingBackTitle: 'No sigues de vuelta',
      notFollowingYouShort: 'No te siguen',
      notFollowingBackShort: 'No sigues',
      notFollowingYouDescription: 'Usuarios que sigues pero no te siguen de vuelta',
      notFollowingBackDescription: 'Usuarios que te siguen pero no sigues de vuelta',
      emptyNotFollowing: 'Todos los usuarios que sigues te siguen de vuelta',
      emptyNotBack: 'Sigues a todos los usuarios que te siguen',
      downloadCsv: 'Descargar CSV'
    },
    comparison: {
      title: 'Comparación con Análisis Anterior',
      followers: 'Seguidores',
      following: 'Seguidos',
      change: 'Cambio',
      comparedWith: 'Comparado con',
      lastAnalysis: 'Último análisis',
      unfollowed: 'Dejaron de seguirte',
      newFollowers: 'Nuevos seguidores',
      youUnfollowed: 'Dejaste de seguir',
      youFollowed: 'Empezaste a seguir'
    },
    historical: {
      title: 'Historial de Análisis',
      count: 'Tienes {count} análisis guardado{count === 1 ? "" : "s"}',
      viewComplete: 'Ver Historial Completo',
      viewHistory: 'Ver Historial',
      viewAnalysis: 'Ver análisis',
      backToHistory: 'Volver al historial',
      noData: 'No hay análisis guardados',
      noDataDescription: 'Una vez que realices tu primer análisis, los datos aparecerán aquí para que puedas comparar cambios a lo largo del tiempo.',
      expand: 'Expandir',
      collapse: 'Contraer',
      backToMain: 'Volver al Inicio',
      newUnfollowers: 'Nuevos que no te siguen',
      newFollowers: 'Nuevos seguidores',
      newUnfollowersDetail: 'Nuevos usuarios que no te siguen',
      newFollowersDetail: 'Nuevos usuarios que ahora te siguen',
      newFollowersFollowedDetail: 'Nuevos seguidores que sigues',
      newFollowersNotFollowedDetail: 'Nuevos seguidores que no sigues',
      lostFollowersDetail: 'Usuarios que dejaron de seguirte',
      newFollowedDetail: 'Usuarios a los que empezaste a seguir',
      showAll: 'Ver todo',
      showLess: 'Ver menos',
      andMore: 'y {count} más',
      selectAnalyses: 'Seleccionar análisis para CSV',
      downloadSelected: 'Descargar seleccionados',
      selectAll: 'Seleccionar todo',
      deselectAll: 'Deseleccionar todo',
      filterFrom: 'Desde',
      filterTo: 'Hasta',
      filterLabel: 'Filtro',
      filteringLabel: 'Aplicando filtro...',
      filteredRange: 'Filtrado: {from} → {to}',
      applyFilters: 'Aplicar',
      selectAllShort: 'Sel. todo',
      clearFilters: 'Limpiar filtros',
      deleteSelected: 'Eliminar seleccionados ({count})',
      bulkDeleteTitle: 'Eliminar {count} análisis seleccionados',
      bulkDeleteDescription: 'Esta acción no se puede deshacer.',
      bulkDeleteConfirm: 'Eliminar ({count})',
      clickToExpand: 'Haz clic en "Expandir" para ver análisis detallado',
      tapToExpand: 'Toca "Expandir" para ver análisis detallado'
    },
    newAnalysis: {
      allNewFollowers: 'Todos los nuevos seguidores',
      newFollowersYouFollow: 'Nuevos seguidores que sigues',
      newFollowersNotFollowed: 'Nuevos seguidores que no sigues',
      lostFollowers: 'Seguidores perdidos',
      newFollowing: 'Nuevos que sigues',
      allNotFollowingYou: 'Todos los que no te siguen de vuelta',
      allNotFollowingBack: 'Todos los que no sigues de vuelta',
      changesSinceLastAnalysis: 'Cambios desde último análisis',
      currentAnalysis: 'Análisis actual'
    },
    delete: {
      confirmTitle: 'Confirmar Eliminación',
      confirmDescription: '¿Estás seguro de que quieres eliminar este análisis del',
      confirm: 'Eliminar',
      deleting: 'Eliminando...'
    },
    common: {
      all: 'Todos',
      success: 'Análisis completado exitosamente',
      successWithComparison: 'Análisis completado y comparado con el anterior',
      firstAnalysisSaved: 'Primer análisis guardado exitosamente',
      error: 'Ocurrió un error durante el análisis',
      errorMissing: 'Falta el ZIP requerido',
      errorJson: 'Error al leer los datos del ZIP',
      errorProcessing: 'Error al procesar los archivos',
      errorNoOriginalData: 'No hay datos originales disponibles para descargar',
      originalFileDownloaded: 'Archivo original {filename} descargado exitosamente',
      downloadOriginalFollowers: 'Descargar archivo original de seguidores',
      downloadOriginalFollowing: 'Descargar archivo original de seguidos',
      errorLoadingHistory: 'Error al cargar análisis históricos',
      errorLoginRequired: 'Debes iniciar sesión para guardar el análisis',
      errorSavingAnalysis: 'Error al guardar el análisis',
      errorDeletingAnalysis: 'Error al eliminar análisis',
      login: 'Iniciar Sesión',
      logout: 'Cerrar Sesión',
      back: 'Volver',
      close: 'Cerrar',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
      yes: 'Sí',
      no: 'No',
      delete: 'Eliminar',
      search: 'Buscar usuarios...',
      copyAll: 'Copiar Todo',
      copy: 'Copiar',
      copied: 'Copiado',
      download: 'Descargar',
      downloadCsv: 'Descargar CSV',
      downloadJson: 'Descargar JSON',
      noResults: 'Sin resultados',
      dismiss: 'Cerrar',
      note: 'Nota',
      tip: 'Consejo',
      user: 'Usuario'
    },
    auth: {
      signIn: 'Iniciar Sesión',
      signUp: 'Registrarse',
      signOut: 'Cerrar Sesión',
      email: 'Correo electrónico',
      password: 'Contraseña',
      confirmPassword: 'Confirmar contraseña',
      forgotPassword: '¿Olvidaste tu contraseña?',
      noAccount: '¿No tienes cuenta?',
      hasAccount: '¿Ya tienes cuenta?',
      loginSuccess: '¡Bienvenido!',
      logoutSuccess: 'Sesión cerrada exitosamente',
      signUpSuccess: '¡Cuenta creada exitosamente! Revisa tu email para confirmar.',
      error: 'Error al iniciar sesión',
      errorInvalidCredentials: 'Email o contraseña incorrectos.',
      errorEmailNotConfirmed: 'Confirmá tu cuenta desde el correo antes de iniciar sesión.',
      errorSignUp: 'Error al crear la cuenta',
      resetPasswordSent: 'Te enviamos un enlace para restablecer tu contraseña',
      resetPasswordSentDescription: 'Si existe una cuenta con ese email, te vamos a enviar un link para resetear la contraseña.',
      resetPasswordError: 'No pudimos enviar el enlace de restablecimiento',
      confirmationResent: 'Te reenviamos el correo de confirmación.',
      confirmationResendError: 'No pudimos reenviar el correo de confirmación.',
      confirmationCodeError: 'El código es inválido o expiró. Intenta nuevamente.',
      errorSignOut: 'Error al cerrar sesión',
      errorUpdatePassword: 'Error al actualizar la contraseña',
      passwordReuseError: 'La nueva contraseña debe ser distinta a la anterior.',
      passwordUpdated: 'Contraseña actualizada exitosamente',
      deleteAccountSuccess: 'Tu cuenta fue eliminada permanentemente.',
      errorDeleteAccount: 'No pudimos eliminar tu cuenta. Intenta nuevamente.'
    },
    languages: {
      es: 'Español',
      en: 'English',
      pt: 'Português'
    },
    modal: {
      confirmAnalysis: 'Confirmar Análisis',
      confirmTitle: 'Confirmar Análisis',
      confirmDescription: '¿Estás listo para procesar tus archivos de Instagram?',
      loginToSave: 'Inicia sesión para guardar y comparar tus análisis',
      continueWithoutLogin: 'Continuar sin iniciar sesión',
      analysisWillBeLost: 'Tu análisis no se guardará y no podrás compararlo con análisis futuros.',
      deleteConfirm: '¿Estás seguro de que quieres eliminar este análisis?',
      deleteWarning: 'Esta acción no se puede deshacer.',
      deleteAnalysis: 'Eliminar análisis del',
      processNote: 'El proceso puede tardar unos segundos dependiendo del tamaño de tus archivos.',
      loginTip: 'Inicia sesión para guardar tu análisis y poder compararlo en el futuro.',
      login: 'Iniciar Sesión',
      loginShort: 'Entrar',
      continue: 'Continuar sin cuenta',
      continueShort: 'Continuar',
      processAnalysis: 'Procesar Análisis'
    },
    csv: {
      notFollowingYou: 'No te sigue',
      notFollowingBack: 'No sigues',
      downloadSuccess: 'CSV descargado con {count} usuarios',
      analysisFrom: 'Análisis del',
      downloadModalTitle: 'Descarga de archivos CSV',
      downloadModalDescription:
        'Descargá la información procesada de este análisis en formato CSV. Elegí una de las 3 opciones según lo que quieras revisar.',
      downloadModalNoteIntro:
        'Este análisis se puede descargar en 3 archivos CSV (cada uno equivale a una pestaña):',
      downloadModalNoteItem1:
        'Followers and following: Contiene la lista de usuarios de followers y following (columnas: username, category).',
      downloadModalNoteItem2:
        'Not following and your fans: Contiene cuentas que seguís y no te siguen + cuentas que te siguen y no seguís (columnas: username, category).',
      downloadModalNoteItem3:
        'Other info: Contiene otras listas disponibles en tu análisis (ej: blocked). Si no hay datos extra, se descarga vacío con headers.',
      downloadModalNoteColumns:
        'Cada CSV tiene 2 columnas separadas por coma: username y category.',
      downloadModalNoteEmpty:
        'Si una categoría no tiene datos disponibles (por ejemplo, blocked users), no aparecerá en el CSV.',
      downloadModalNoteSheets:
        'Abrilo en Excel o Google Sheets y filtrá por category para ver la lista que te interese.',
      downloadModalFilesTitle: 'Archivos disponibles',
      downloadModalFile1Title: 'Followers and following',
      downloadModalFile1Subtitle: '(category = follower / following)',
      downloadModalFile2Title: 'Not following and your fans',
      downloadModalFile2Subtitle: '(category = not_following_you / your_fans)',
      downloadModalFile3Title: 'Other info',
      downloadModalFile3Subtitle: 'Other lists (e.g. blocked) if available'
    },
    loginSection: {
      optionalAccount: 'Cuenta opcional',
      whyCreate: '¿Por qué crear una cuenta?',
      saveHistory: 'Guarda tu historial de análisis',
      compareChanges: 'Compara cambios en seguidores',
      seeTrends: 'Observa tendencias y patrones',
      loginWithGoogle: 'Iniciar sesión con Google',
      continueWithout: 'Continuar sin crear cuenta'
    },
    authModal: {
      subtitle: 'Inicia sesión o crea una cuenta para guardar tu historial',
      continueWithGoogle: 'Continuar con Google',
      or: 'o',
      signInTab: 'Iniciar Sesión',
      signUpTab: 'Crear Cuenta',
      resetTitle: 'Restablecer contraseña',
      resetDescription: 'Te enviaremos un enlace para crear una nueva contraseña.',
      resetSpamHint: 'Si no lo ves, revisá spam/promociones.',
      resetButton: 'Enviar enlace',
      resetCancel: 'Volver a iniciar sesión',
      sendingReset: 'Enviando...',
      resetConfirmTitle: 'Crear nueva contraseña',
      resetConfirmDescription: 'Ingresá tu nueva contraseña para recuperar el acceso.',
      resetConfirmButton: 'Guardar contraseña',
      resetLinkExpired: 'El enlace para restablecer la contraseña expiró. Solicitá uno nuevo.',
      resetRequestLink: 'Solicitar nuevo enlace',
      passwordMismatch: 'Las contraseñas no coinciden.',
      emailPlaceholder: 'tu@email.com',
      passwordPlaceholder: '••••••••',
      fullName: 'Nombre completo',
      fullNamePlaceholder: 'Tu nombre',
      signingIn: 'Iniciando...',
      signingUp: 'Creando...',
      emailConfirmTitle: 'Revisá tu correo para confirmar la cuenta',
      emailConfirmDescription: 'Te enviamos un código de 6 dígitos a {email}.',
      emailConfirmHint: 'Si no lo encontrás, revisá spam/promociones.',
      emailConfirmExpiry: 'El código vence en 1 hora.',
      emailConfirmCodeLabel: 'Código de verificación',
      emailConfirmCodePlaceholder: '000000',
      emailConfirmCodeButton: 'Confirmar código',
      emailConfirmingCode: 'Verificando...',
      goToSignIn: 'Ir a iniciar sesión',
      resendConfirmation: 'Reenviar correo de confirmación',
      resendingConfirmation: 'Reenviando...'
    },
    analysis: {
      detailedAnalysis: 'Análisis Detallado',
      downloadCompleteCSV: 'Descargar CSV Completo',
      changesSincePrevious: 'Resumen de cambios',
      accountDataTitle: 'Datos de tu cuenta',
      followersTotalLabel: 'Seguidores totales',
      followingTotalLabel: 'Seguidos totales',
      currentAnalysis: 'Análisis actual',
      noFollowYouShort: 'No te siguen',
      noFollowBackShort: 'No sigues',
      ratio: 'Ratio',
      loadingHistory: 'Cargando historial...',
      noPreviousAnalyses: 'No hay análisis previos',
      changeExplanation: 'Explicación de Cambios: ¿Cómo llegamos a {change} seguidores?',
      comparedWith: 'Comparando con análisis del {date}',
      previousFollowers: 'Seguidores anteriores',
      newFollowersLabel: 'Seguidores nuevos',
      newFollowedLabel: 'Nuevos seguidos',
      newFollowedDetail: 'Nuevos usuarios que sigues',
      lostFollowersLabel: 'Seguidores perdidos',
      currentFollowersLabel: 'Seguidores actuales',
      viewList: 'Ver lista',
      newFollowersSection: 'Nuevos Seguidores',
      lostFollowersSection: 'Seguidores Perdidos',
      newFollowingSection: 'Nuevas Cuentas que Sigues',
      allNewFollowers: 'Todos',
      mutualFollowers: 'Mutuos',
      notFollowingNew: 'No sigues',
      allNewFollowersDesc: 'Todos los nuevos seguidores',
      newMutualFollowersDesc: 'Nuevos seguidores que también sigues',
      newNotFollowingDesc: 'Nuevos seguidores que no sigues',
      usersWhoUnfollowed: 'Usuarios que dejaron de seguirte',
      noUnfollowers: '¡Excelente! No has perdido seguidores',
      renamedFollowersSection: 'Usuarios que cambiaron su @',
      renamedFollowersList: 'Cambios detectados de @',
      renamedFollowersTab: 'Renombrados',
      noRenamedFollowers: 'No se detectaron cambios de @',
      accountsYouStartedFollowing: 'Cuentas que empezaste a seguir',
              noNewFollowing: 'No has seguido nuevas cuentas',
      notFollowingYouTab: 'No te siguen',
      notFollowingBackTab: 'No sigues',
      fansTitle: 'Tus fans',
      dialogSubtitle: 'Comparado con tu análisis anterior',
      dialogDescriptionNewFollowers: 'Personas que comenzaron a seguirte desde el último análisis.',
      dialogDescriptionLostFollowers: 'Personas que dejaron de seguirte desde el último análisis.',
      dialogDescriptionNotFollowingYou: 'Personas que seguís pero que no te siguen de vuelta.',
      dialogDescriptionFans: 'Personas que te siguen pero que vos no seguís.',
      dialogDescriptionPendingRequests: 'Solicitudes de seguimiento que enviaste y aún no fueron aceptadas.',
      dialogDescriptionRequestsReceived: 'Personas que solicitaron seguirte y aún no aceptaste ni rechazaste.',
      dialogDescriptionFavorites: 'Perfiles que marcaste como favoritos en Instagram.',
      dialogDescriptionBlocked: 'Perfiles que bloqueaste en Instagram.',
      dialogDescriptionCloseFriends: 'Personas incluidas en tu lista de Close Friends.',
      dialogDescriptionHiddenStories: 'Perfiles a los que les ocultaste tus historias.',
      renamedFrom: 'Antes: @{username}',
      netChangeLabel: 'Cambio neto',
      dialogEmpty: 'No hay usuarios en esta categoría',
      emptyFirstTitle: 'Este es tu primer análisis',
      emptyFirstDescription: 'Todavía no hay un análisis previo para comparar y detectar cambios.',
      emptyLostTitle: '¡Excelente!',
      emptyLostDescription: 'No perdiste seguidores desde el último análisis.',
      emptyNewTitle: 'Sin cambios por ahora',
      emptyNewDescription: 'No hubo nuevos seguidores desde el último análisis.',
      emptyGenericTitle: 'No hay usuarios',
      emptyGenericDescription: 'No hay usuarios en esta categoría.',
      openProfile: 'Abrir perfil',
        notFollowingYouDescription: 'Usuarios que sigues pero no te siguen de vuelta',
        notFollowingBackDescription: 'Usuarios que te siguen pero no sigues de vuelta',
        usersYouFollowButDontFollow: 'Usuarios que sigues pero no te siguen',
        excellentAllFollow: '¡Excelente! Todos los usuarios que sigues te siguen de vuelta',
      usersWhoFollowYouButYouDont: 'Usuarios que te siguen pero no sigues',
      youFollowAllWhoFollow: 'Sigues a todos los usuarios que te siguen',
      usersWhoStartedFollowing: 'Usuarios que empezaron a seguirte',
      noNewFollowersThisPeriod: 'No hay nuevos seguidores en este período',
      andMoreUsers: 'Y {count} usuarios más...',
      statsSectionTitle: 'Estadísticas recientes',
      statsSectionDescription: 'Visualiza la evolución de tus seguidores y seguidos.',
      statsNoDataTitle: 'Todavía no hay estadísticas',
      statsNoDataDescription: 'Cuando realices tu primer análisis, acá vas a poder ver la evolución de tus seguidores y comparar cambios en el tiempo.',
      chartZoomHint: 'Arrastra el control inferior para hacer zoom o desplazar el período.',
      followersEvolutionTitle: 'Evolución de seguidores',
      newLostEvolutionTitle: 'Evolución de nuevos seguidores y perdidos',
      newLostEvolutionNoData: 'Necesitás al menos dos análisis para ver esta evolución.',
      timelineTitle: 'Timeline de actividad',
      timelineSubtitle: 'Movimientos detectados de los análisis realizados.',
      timelineTab: 'Actividad reciente',
      activityTitle: 'Actividad reciente',
      activityHelpAria: 'Ayuda de actividad reciente',
      activityHelpTitle: '¿Qué significan estos eventos?',
      activityHelpMutual: 'Mutual: ambos se siguen mutuamente.',
      activityHelpRename: 'Cambio de Nombre: el usuario cambió su nombre de Instagram.',
      activityHelpNote: 'Esto se muestra por separado para evitar confusiones, ya que un cambio de nombre puede parecer un nuevo seguidor o un nuevo seguido, cuando en realidad es la misma cuenta.',
      firstAnalysisInfoAria: 'Información del primer análisis',
      firstAnalysisInfo: 'Este es tu primer análisis. En el próximo análisis se habilitará una nueva pestaña con la actividad reciente.',
      activityFilters: {
        all: 'Todos',
        newFollowers: 'Seguidores nuevos',
        lostFollowers: 'Seguidores perdidos',
        newFollowed: 'Nuevos seguidos',
        mutual: 'Mutual',
        renames: 'Cambios de nombre'
      },
      activityNewFollower: '@{username} empezó a seguirte',
      activityLostFollower: '@{username} dejó de seguirte',
      activityNewFollowed: 'Empezaste a seguir a @{username}',
      activityRenameLabel: 'Cambio de nombre',
      mutualBadge: 'Mutual',
      activityFilterEmpty: 'No hay eventos para este filtro en el último análisis.',
      activityEmptyTitle: 'Sin actividad reciente',
      activityEmptyDescription: 'No se detectaron cambios desde el último análisis.',
      moreInsightsTab: 'Más información',
      moreInsightsNote: '**Nota**: estas listas muestran la **cantidad total de usuarios** en cada categoría, no solo los cambios recientes.',
      moreInsightsNoteFirst: '**Nota**: estas listas muestran la **cantidad total de usuarios** en cada categoría.',
      moreInsightsDialogSubtitle: 'Lista completa (estado actual)',
      dialogComparedLine: 'Cambios comparados con el análisis del {date}.',
      dialogCurrentStateLine: 'Esta lista muestra el estado actual de tu cuenta.',
      dialogNoComparison: 'No hay un análisis previo para comparar.',
      moreInsights: {
        pendingRequests: 'Solicitudes pendientes',
        requestsReceived: 'Solicitudes recibidas',
        favorites: 'Favoritos',
        blocked: 'Bloqueados',
        closeFriends: 'Mejores amigos',
        hiddenStories: 'Historias ocultas'
      },
      viewMore: 'Ver más',
      timelineNeedsData: 'Necesitas al menos dos análisis para ver el timeline de movimientos.',
      timelineNoChanges: 'No hubo cambios desde tu último análisis.',
      comparisonLabel: 'Comparado con el último análisis guardado del {date}',
      noPreviousAnalysis: 'Sin análisis previo',
      timelineNewFollower: 'Nuevo seguidor',
      timelineLostFollower: 'Dejó de seguirte',
      timelineRenamedFollower: 'Cambio de nombre',
      timelineEmpty: 'No se detectaron movimientos recientes.',
      timelineRangeLabel: 'Mostrar últimos análisis',
      timelineRangeLatest: 'Último análisis',
      timelineRangeAll: 'Todos',
      timelineRangeOption: '{count} análisis',
      timelineSingleAnalysis: 'Solo tienes un análisis guardado del {date}. Realiza otro para ver movimientos.'
    },
    instructionSteps: {
      step1Title: 'Abre Instagram en tu móvil',
      step1Description: 'Ve a tu perfil y toca el menú (☰) en la esquina superior derecha',
      step2Title: 'Configuración y privacidad',
      step2Description: 'Selecciona \'Configuración y privacidad\' en el menú',
      step3Title: 'Centro de cuentas',
      step3Description: 'Toca \'Centro de cuentas\' y luego \'Tu información y permisos\'',
      step4Title: 'Descargar tu información',
      step4Description: 'Selecciona \'Descargar tu información\' y luego \'Descargar o transferir información\'',
      step5Title: 'Solicitar descarga',
      step5Description: 'Elige \'Algunas de tu información\' → Selecciona \'Seguidores y siguiendo\' → Crear archivos',
      step6Title: 'Esperar el email',
      step6Description: 'Instagram te enviará un email cuando esté listo (puede tardar hasta 48 horas)',
      step7Title: 'Subí el ZIP aquí',
      step7Description: 'Descarga el ZIP del email y súbelo en UnFollowed.',
      requiredFiles: 'ZIP necesario',
      followersList: 'Tu información de seguidores',
      followingList: 'Tu información de seguidos',
      tipMessage: 'Con el ZIP es suficiente; no hace falta extraerlo.'
    },
    userSettings: {
      accountSettings: 'Configuración de cuenta',
      manageAccount: 'Gestiona tu cuenta y configuración de seguridad',
      profile: 'Perfil',
      password: 'Contraseña',
      danger: 'Peligro',
      fullName: 'Nombre completo',
      email: 'Email',
      loginMethod: 'Método de inicio de sesión',
      loginMethodEmail: 'Email y contraseña',
      profileChangeNote: 'Para cambiar tu información personal, contacta al soporte.',
      newPassword: 'Nueva contraseña',
      confirmPassword: 'Confirmar contraseña',
      oauthPasswordMessage: 'Esta cuenta se creó usando {provider}. Administra tu contraseña desde tu cuenta de {provider}.',
      updating: 'Actualizando...',
      updatePassword: 'Actualizar contraseña',
      dangerZone: 'Zona de peligro:',
      actionCannotBeUndone: 'Esta acción no se puede deshacer.',
      deleteAccount: 'Eliminar cuenta',
      confirmDeleteText: 'Para confirmar, escribe "ELIMINAR" en el campo de abajo:',
      confirmDeletion: 'Confirmar eliminación',
      deleting: 'Eliminando...',
      user: 'Usuario'
    },
    feedback: {
      title: 'Comparte tu feedback',
      subtitle: 'Contanos qué mejorar, reportá un problema o deja tu calificación.',
      categoryLabel: '¿Qué tipo de comentario querés dejar?',
      categories: {
        issue: 'Problemas',
        idea: 'Idea o sugerencia',
        other: 'Otros'
      },
      ratingLabel: 'Calificación general',
      ratingHint: '1 = Muy mala experiencia, 5 = Excelente',
      messageLabel: 'Tu mensaje',
      messagePlaceholder: 'Escribe aquí los detalles, links o pasos para reproducir el problema...',
      emailLabel: 'Email de contacto (opcional)',
      emailPlaceholder: 'te@ejemplo.com',
      submit: 'Enviar feedback',
      submitting: 'Enviando...',
      successTitle: '¡Gracias por tu feedback!',
      successDescription: 'Tu comentario nos ayuda a mejorar Insta View Explorer.',
      errorTitle: 'No pudimos enviar tu feedback',
      errorDescription: 'Intentá nuevamente en unos segundos.'
    },
    userList: {
      ofUsers: '{count} de {total} usuarios',
      downloadedCSV: 'CSV descargado con {count} usuarios'
    },
    json: {
      downloadTitle: 'Descargar Archivos JSON',
      downloadDescription: 'Descarga los archivos JSON originales que subiste para este análisis. Estos son los datos sin procesar de Instagram.',
      analysisDateNote: 'Archivos del análisis realizado el {date}',
      availableFiles: 'Archivos disponibles',
      followersDescription: 'Lista completa de tus seguidores',
      followingDescription: 'Lista completa de cuentas que sigues'
    },
    adminDashboard: {
      menuLink: 'Panel de administración',
      badge: 'Solo administrador',
      title: 'Panel de visibilidad de usuarios',
      subtitle: 'Monitorea en tiempo real cómo se usa la aplicación, desde dónde ingresan y cuánto duran las sesiones. Los datos se actualizan automáticamente desde Supabase.',
      refresh: 'Actualizar datos',
      refreshing: 'Actualizando...',
      backToApp: 'Volver a la aplicación',
      loginRequired: 'Inicia sesión para continuar',
      loginHint: 'Este panel está disponible únicamente para la cuenta administradora.',
      loading: 'Preparando métricas del panel...',
      noAccess: 'No tienes acceso al panel',
      noAccessHint: 'Solicita acceso al administrador para poder ver estas métricas.',
      errorTitle: 'No pudimos obtener los datos',
      errorDescription: 'Refresca el panel para intentar nuevamente.',
      retry: 'Reintentar',
      stats: {
        totalUsers: 'Usuarios totales',
        userHelper: 'Usuarios registrados en Supabase',
        activeSessions: 'Sesiones activas',
        activeHelper: 'Sesiones únicas (ventana 5 min)',
        avgSession: 'Duración promedio',
        avgHelper: 'Basado en las últimas sesiones',
        returningRate: 'Tasa de retorno',
        returningHelper: 'Usuarios con 2+ sesiones en 30 días',
        sessions30: 'Sesiones (30 días)',
        sessionsHelper: 'Sesiones únicas registradas el último mes',
        uniqueLogins30: 'Usuarios únicos (30 días)',
        uniqueLogins30Helper: 'Usuarios que iniciaron sesión al menos una vez',
        newUsers: 'Usuarios nuevos',
        newUsersHelper: 'Creados en los últimos 30 días',
        loginEvents: 'Ingresos recientes (últimos 14 días)'
      },
      signupHealth: {
        title: 'Salud del signup (30 días)',
        subtitle: 'Embudo de activación desde registro hasta primer análisis.',
        signups: 'Signups',
        verified: 'Verificados',
        firstSession: 'Primera sesión',
        firstAnalysis: 'Primer análisis',
        avgSignupToVerified: 'Promedio signup -> verificado',
        avgVerifiedToSession: 'Promedio verificado -> primera sesión',
        avgSessionToAnalysis: 'Promedio primera sesión -> primer análisis'
      },
      details: {
        user: 'Usuario',
        country: 'País',
        lastAccess: 'Último acceso',
        sessionsCount: 'Sesiones',
        firstAccess: 'Primer acceso',
        browser: 'Navegador',
        device: 'Dispositivo',
        os: 'Sistema operativo',
        mobile: 'OS mobile',
        firstSession: 'Primera sesión',
        lastSession: 'Última sesión',
        noAccess: 'Sin registros',
        waitingVerification: 'Esperando verificación',
        empty: 'No hay usuarios para mostrar.',
        pageInfo: 'Mostrando {from}-{to} de {total}',
        rowsPerPage: 'Filas por página',
        previousPage: 'Anterior',
        nextPage: 'Siguiente',
        countryFilter: 'País',
        providerFilter: 'Proveedor',
        allCountries: 'Todos los países',
        allProviders: 'Todos los proveedores',
        all: 'Todos',
        from: 'Desde',
        to: 'Hasta',
        filtersTitle: 'Filtros',
        filtersSubtitle: 'Aplicá filtros globales para analizar las métricas principales del panel.',
        firstAccessFrom: 'Primer acceso desde',
        firstAccessTo: 'Primer acceso hasta',
        lastAccessFrom: 'Último acceso desde',
        lastAccessTo: 'Último acceso hasta',
        firstAccessModalDescription: 'Seleccioná un rango para filtrar por fecha de primer acceso.',
        lastAccessModalDescription: 'Seleccioná un rango para filtrar por fecha de último acceso.',
        clearFilters: 'Limpiar filtros',
        actions: 'Acciones',
        copyUserId: 'Copiar user id',
        copyEmail: 'Copiar email',
        openSupabase: 'Abrir en Supabase',
        filterByCountry: 'Filtrar por país',
        filterByProvider: 'Filtrar por proveedor',
        scrollableTable: 'Tabla desplazable de detalle de usuarios',
        totalUsersDescription: 'Listado de usuarios registrados.',
        activeSessionsDescription: 'Usuarios con sesiones activas en los últimos 5 minutos.',
        newUsersDescription: 'Usuarios creados en los últimos 30 días.',
        returningRateDescription: 'Usuarios con 2 o más sesiones en los últimos 30 días.',
        sessions30Description: 'Usuarios con sesiones registradas en los últimos 30 días.',
        uniqueLogins30Description: 'Usuarios que iniciaron sesión al menos una vez en los últimos 30 días.',
        countriesDescription: 'Usuarios agrupados por país y su última conexión.',
        browsersDescription: 'Usuarios agrupados por navegador y su última conexión.'
      },
      charts: {
        logins: 'Ingresos',
        sessions: 'Sesiones',
        loginTrend: 'Actividad de ingresos (14 días)',
        loginTrendHelper: 'Eventos registrados en las últimas dos semanas',
        loginTrendNote: 'Incluye todos los eventos de login (no únicos)',
        sessionDuration: 'Duración por sesión',
        sessionsByHour: 'Sesiones por hora',
        devices: 'Dispositivos',
        os: 'Sistemas operativos',
        mobileOs: 'Mobile (iOS / Android)',
        countries: 'Principales países',
        browsers: 'Navegadores',
        analysisLeaderboard: 'Análisis realizados',
        topUsers: 'Usuarios más activos',
        sessions30Note: 'Basado en cantidad de sesiones (últimos 30 días).',
        uniqueSessions30Title: 'Sesiones únicas (30 días)',
        uniqueSessions30Note: 'Cuenta usuarios únicos por categoría.',
        allSessionsTitle: 'Todas las sesiones (histórico)',
        allSessionsNote: 'Basado en todas las sesiones registradas.',
        sessionsLabel: 'sesiones',
        lastLogin: 'Último acceso: {date}',
        noLoginRecorded: 'Sin registros',
        countryUnknown: 'País desconocido',
        analysisCountLabel: 'análisis'
      },
      emptyStates: {
        noLogins: 'Todavía no hay eventos suficientes.',
        noCountries: 'Aún no hay datos de países.',
        noSessions: 'Sin sesiones registradas.',
        noDevices: 'No hay datos de dispositivos.',
        noOs: 'No hay datos de sistemas operativos.',
        noMobileOs: 'Sin sesiones móviles suficientes.',
        noBrowsers: 'Aún no hay datos de navegadores.',
        noLoginUsers: 'Todavía no hay ingresos registrados por usuario.',
        noUsers: 'Todavía no hay usuarios destacados.',
        noAnalysisUsers: 'Todavía no hay análisis registrados.'
      },
      feedback: {
        title: 'Feedback de usuarios',
        subtitle: 'Lo que la comunidad estuvo compartiendo recientemente.',
        empty: 'Todavía no recibimos feedback.',
        anonymous: 'Usuario anónimo',
        rating: 'Calificación: {rating}/5',
        contactLabel: 'Contacto',
        categoryLabel: 'Categoría',
        sortLabel: 'Ordenar',
        sortAsc: 'Menor a mayor',
        sortDesc: 'Mayor a menor'
      }
    },
    sidebar: {
      navigation: 'Navegación',
      instructions: 'Instrucciones',
      instructionsDesc: 'Como usar la app',
      instructionsShort: 'Instrucciones',
      upload: 'Subir archivos',
      uploadDesc: 'Sube y analiza tus datos',
      uploadShort: 'Subir',
      history: 'Historial',
      historyDesc: 'Ver análisis anteriores',
      historyShort: 'Historial',
      stats: 'Estadísticas',
      statsDesc: 'Ver gráficos y timeline',
      statsShort: 'Estadísticas',
      blog: 'Blog',
      blogDesc: 'Artículos y novedades',
      analysis: 'Análisis Detallado',
      analysisDesc: 'Ver análisis en detalle',
      settings: 'Customizaciones',
      language: 'Idioma',
      theme: 'Tema',
      feedback: 'Enviar feedback',
      feedbackDesc: 'Reporta problemas o deja sugerencias'
    },
    mobileHome: {
      title: 'Bienvenido',
      titleName: 'Bienvenido, {name}',
      titleBack: 'Bienvenido de nuevo',
      titleBackName: 'Bienvenido de nuevo, {name}',
      subtitle: '¿Listo para explorar tus insights de Instagram?',
      cta: 'Empecemos',
      home: 'Inicio',
      homeDesc: 'Pantalla de bienvenida'
    }
  },
  en: {
    header: {
      title: 'UnFollowed',
      subtitle: 'Analyze your followers and who you follow',
      login: 'Login',
      logout: 'Logout',
      settings: 'Settings',
      theme: {
        light: 'Light',
        dark: 'Dark',
        system: 'System'
      }
    },
    welcome: {
      title: 'Analyze Your Instagram Followers',
      subtitle: 'Discover who doesn\'t follow you back and who does',
      description: 'Upload your Instagram ZIP to get a detailed analysis of your followers and who you follow.',
      followSteps: 'Follow these steps to get your Instagram data'
    },
      landing: {
      hero: {
        title: 'Safely discover who unfollowed you on Instagram.',
        titleLeading: 'Safely discover who',
        titleAccent: 'unfollowed you on Instagram.',
        subtitle: 'Turn your Instagram follower data into actionable insights in minutes.',
        primaryCta: 'Sign in to get started',
        howItWorksCta: 'How it works',
        securityChip: 'You never enter your Instagram password in our app; everything happens on Instagram’s official website.'
      },
      seo: {
        title: 'UnFollowed | See who unfollowed you on Instagram',
        description: 'UnFollowed helps you see who unfollowed you on Instagram with a secure analysis based on the official Instagram ZIP.',
        keywords: 'UnFollowed, Instagram, unfollowed in instagram, unfollowed on instagram, followers, unfollowers, follower analysis'
      },
      steps: {
        title: 'How it works'
      },
      howItWorks: {
        demoCta: 'View interactive demo',
        downloadButton: 'Download data',
        downloadingLabel: 'Downloading...',
        downloadComplete: 'Download complete',
        downloadAgain: 'Download again',
        dropLabel: 'Drop your file here',
        uploadUploaded: 'Uploaded ✓',
        uploadProcessing: 'Processing...',
        uploadReady: 'Ready',
        play: 'Play',
        pause: 'Pause',
        steps: {
          download: {
            title: 'Download your data',
            subtitle: '',
            description: 'Request your data export from Instagram to access your information.'
          },
          upload: {
            title: 'Upload your data',
            subtitle: '',
            description: 'Drag and drop the ZIP file you downloaded from Instagram to analyze it.'
          },
          results: {
            title: 'See results instantly',
            subtitle: '',
            description: 'Identify who unfollowed you on Instagram and view your metrics easily.'
          }
        },
        resultsTabs: {
          notFollowing: 'Not following',
          notFollowed: 'Not following you'
        },
        uploadSuccess: 'Files uploaded successfully',
        actions: {
          copyUser: 'Copy username',
          openProfile: 'Open profile'
        }
      },
      trust: {
        title: 'Privacy first',
        privacy: 'Your files never leave your device',
        privacyDescription: 'Only you can view your files and delete them anytime.',
        highlight1: 'End-to-end encryption',
        highlight2: 'Guaranteed Instagram compatibility'
      },
      features: {
        title: 'Key features',
        cards: {
          notFollowingBack: {
            title: 'Lost followers',
            description: 'See everyone you follow that doesn’t follow you'
          },
          newFollowers: {
            title: 'New followers',
            description: 'Spot who recently started following you'
          },
          stats: {
            title: 'Detailed metrics',
            description: 'Charts and metrics to track your growth'
          },
          timeline: {
            title: 'Timeline view',
            description: 'Understand how your audience changes over time'
          }
        }
      },
      faq: {
        title: 'Frequently asked questions',
        items: {
          safeUnfollowed: {
            question: '🔍 How can I see who unfollowed me on Instagram for free and safely?',
            answer:
              'With UnFollowed, you can see who unfollowed you on Instagram for free and safely.\nJust download your Instagram information as a ZIP file from your account settings and upload it to the app. UnFollowed analyzes your followers and following to show who unfollowed you—without logging into Instagram, without sharing your password, and without paid tools.'
          },
          accountSafety: {
            question: '🛡️ Can using this app get my Instagram account blocked or suspended?',
            answer:
              'No. This app is 100% safe and does not violate Instagram’s rules.\nUnlike other similar apps:\n• We never ask for your username or password\n• We don’t access your account directly\n• We don’t scrape data\n• We don’t run automated actions\nThe analysis uses only the official data Instagram lets you download, keeping your account safe from blocks or suspensions.'
          },
          accuracy: {
            question: '🎯 How accurate is the analysis?',
            answer:
              'It’s very accurate because it uses the official data exported by Instagram.\nThe more often you upload updated files, the more insight you’ll have into your metrics over time, including changes since your last analysis.'
          },
          autoTracking: {
            question: '🤖 Does the app track automatically?',
            answer:
              'No, and that’s intentional. To keep your account safe and avoid any risk, the app doesn’t connect to Instagram automatically.\nWhenever you want an updated analysis, just upload the new ZIP file Instagram provides.'
          },
          whyDownload: {
            question: '📦 Why do I have to download the file from Instagram?',
            answer:
              'Instagram doesn’t allow external apps to access your private data directly.\nDownloading the ZIP is the official, safe way to get your data and keep full control of your account.'
          },
          generationTime: {
            question: '⏱️ How long does Instagram take to generate the file?',
            answer:
              'Usually a few minutes, up to about an hour, depending on how much data your account has.\nWhen it’s ready, Instagram notifies you by email.'
          },
          techKnowledge: {
            question: '🙋‍♀️ Do I need technical knowledge to use the app?',
            answer:
              'Not at all. It’s very simple: download the ZIP from Instagram, upload it, and you’re done.\nWe’ll handle the rest.'
          },
          unfollowNotifications: {
            question: '🔔 Does Instagram notify you when someone unfollows you?',
            answer:
              'No. Instagram doesn’t send notifications when someone unfollows you.\nThat’s why UnFollowed is a simple, safe way to see who unfollowed you without manually comparing your followers and following lists.'
          },
          blockedUsers: {
            question: '🚫 Can UnFollowed show who blocked you on Instagram?',
            answer:
              'UnFollowed is built to detect who unfollowed you using the official data you download from Instagram.\nIf someone blocks you, Instagram stops showing you their profile, posts, and stories. However, Instagram does not include blocking information in the downloadable data, so no app (including UnFollowed) can provide an exact list of who blocked you.\nWhat UnFollowed can do is track changes in your followers and identify who unfollowed you, which in some cases may coincide with a block.'
          }
        }
      },
      benefits: {
        items: {
          secure: {
            title: '100% secure',
            subtitle: 'Your data, your control.'
          },
          unfollowed: {
            title: 'Find out who unfollowed you',
            subtitle: 'Clearly identify who stopped following you.'
          },
          noAccess: {
            title: 'No Instagram access required',
            subtitle: 'You never share your password with this app.'
          },
          free: {
            title: '100% free',
            subtitle: 'Use all features at no cost.'
          }
        }
      },
      cta: {
        title: 'Ready to uncover your insights?',
        description: 'Start now and get detailed follower analytics in minutes.',
        button: 'Start now'
      },
      footer: {
        copyright: '© 2026 UnFollowed. All rights reserved.',
        disclaimer: 'We are not affiliated with Instagram or Meta.'
      },
      footerLinks: {
        navigation: 'Navigation',
        home: 'Home',
        howItWorks: 'How it works',
        faq: 'FAQ',
        legal: 'Legal',
        terms: 'Terms & Conditions',
        privacy: 'Privacy Policy',
        contactTitle: 'Have questions?',
        contactCta: 'Contact us'
      }
    },
    termsPage: {
      title: 'Terms and Conditions for UnFollowed',
      intro: 'Please read these Terms and Conditions carefully before using our service.',
      effectiveDate: 'Effective Date: 01/01/2026',
      sections: {
        introduction: {
          title: '1. Introduction',
          paragraph1:
            'UnFollowed provides a tool that allows users to analyze their Instagram followers and unfollowers based solely on the official data downloaded by the user from Instagram.',
          paragraph2: 'By accessing or using UnFollowed, you agree to be bound by these Terms and Conditions.'
        },
        use: {
          title: '2. Use of the Service',
          paragraph1:
            'UnFollowed allows users to upload Instagram data files obtained directly from Instagram in order to identify unfollowers and follower changes over time.',
          bullets: {
            noPassword: 'We do not require your Instagram username or password.',
            noDirectAccess: 'We do not access Instagram accounts directly.',
            noAutomation: 'We do not perform scraping, automation, or actions on behalf of users.',
            userProvided: 'All analysis is performed exclusively using the data voluntarily provided by the user.'
          }
        },
        privacy: {
          title: '3. Privacy',
          paragraph1: 'We respect your privacy and are committed to protecting your data.',
          paragraph2:
            'Please refer to our Privacy Policy for detailed information on how we collect, process, store, and protect your information.'
        },
        responsible: {
          title: '4. Responsible Use',
          paragraph1:
            'Users agree to use UnFollowed responsibly and in compliance with all applicable laws and regulations.',
          paragraph2:
            'You may not use the service for illegal, harmful, abusive, defamatory, or invasive purposes, including but not limited to harassment or violation of third-party privacy rights.',
          paragraph3:
            'UnFollowed is not responsible for how users interpret or use the information provided by the service. Users are solely responsible for their actions and any consequences resulting from the use of the app.'
        },
        limitations: {
          title: '5. Service Limitations',
          paragraph1:
            'UnFollowed provides insights based on data made available by Instagram through its data export tools.',
          bullets: {
            noGuarantees: 'We do not guarantee absolute accuracy, completeness, or uninterrupted availability of the service.',
            resultsMayVary: 'Results may vary depending on the quality, completeness, and timing of the uploaded data.',
            instagramChanges:
              'Changes made by Instagram to its data structure or export policies may affect the functionality or accuracy of the service.'
          },
          paragraph2: 'Users acknowledge and accept these limitations.'
        },
        changes: {
          title: '6. Changes to the Terms',
          paragraph1: 'We reserve the right to update or modify these Terms and Conditions at any time.',
          paragraph2: 'Any changes will be published on this page and will take effect immediately upon posting.',
          paragraph3:
            'By continuing to use UnFollowed after changes are made, you agree to be bound by the updated Terms and Conditions.'
        }
      }
    },
    privacyPage: {
      title: 'Privacy Policy for UnFollowed',
      intro:
        'Your privacy matters to us. This Privacy Policy explains how UnFollowed collects, uses, and protects your information.',
      effectiveDate: 'Effective Date: 01/01/2026',
      sections: {
        introduction: {
          title: '1. Introduction',
          paragraph1:
            'UnFollowed lets users analyze Instagram follower data using files downloaded from Instagram and voluntarily uploaded by the user.',
          paragraph2:
            'By using UnFollowed, you agree to the collection and use of information described in this Privacy Policy.'
        },
        collect: {
          title: '2. Information We Collect',
          paragraph1: 'We collect only the minimum data needed to provide the service.',
          subheadingProvided: 'a) Information You Provide',
          bullets: {
            instagramFiles: 'Instagram data files you download from Instagram (such as the ZIP export).',
            technicalMetadata:
              'Technical metadata required to process the analysis (e.g., file structure or timestamps).'
          },
          subheadingNotRequested: 'We do not request:',
          notBullets: {
            noUsername: 'Your Instagram username or password.',
            noDirectAccess: 'Direct access to your Instagram account.'
          }
        },
        use: {
          title: '3. How We Use Your Information',
          paragraph1: 'The information you provide is used only to:',
          bullets: {
            analyze: 'Analyze follower and unfollower data.',
            generate: 'Generate insights and visualizations within the app.',
            improve: 'Improve the performance and reliability of the service.'
          },
          paragraph2: 'We do not use your data for advertising, profiling, or resale.'
        },
        processing: {
          title: '4. Data Processing and Storage',
          paragraph1: 'All data is processed solely to deliver the analysis you request.',
          paragraph2: 'Uploaded files are not shared with third parties.',
          paragraph3: 'You can delete your uploaded data at any time.',
          paragraph4:
            'We take reasonable technical and organizational measures to protect your information from unauthorized access, loss, or misuse.'
        },
        thirdParty: {
          title: '5. Third-Party Services',
          paragraph1:
            'UnFollowed does not integrate with Instagram’s API and does not perform scraping or automation.',
          paragraph2:
            'We may use trusted infrastructure providers (such as hosting or analytics tools) only to operate and improve the service. These providers are bound by confidentiality and data-protection obligations.'
        },
        cookies: {
          title: '6. Cookies and Analytics',
          paragraph1:
            'We may use basic analytics and cookies to understand how users interact with the app (e.g., page views or session duration).',
          bullets: {
            anonymized: 'Data is anonymized.',
            noFiles: 'Does not include your uploaded Instagram files.',
            improveExperience: 'Used only to improve user experience and performance.'
          },
          paragraph2: 'You can disable cookies in your browser settings.'
        },
        retention: {
          title: '7. Data Retention',
          paragraph1:
            'We retain user data only for as long as necessary to provide the service or until the user deletes it.',
          paragraph2:
            'Once data is deleted, it is permanently removed from our systems within a reasonable timeframe.'
        },
        rights: {
          title: '8. Your Rights',
          paragraph1: 'Depending on your jurisdiction, you may have the right to:',
          bullets: {
            access: 'Access your data.',
            delete: 'Request deletion of your data.',
            restrict: 'Restrict or object to processing.'
          },
          paragraph2: 'You can exercise these rights by contacting us.'
        },
        changes: {
          title: '9. Changes to This Privacy Policy',
          paragraph1: 'We may update this Privacy Policy from time to time.',
          paragraph2: 'Any changes will be posted on this page and will take effect immediately upon publication.',
          paragraph3: 'Continued use of UnFollowed after changes constitutes acceptance of the updated policy.'
        },
        contact: {
          title: '10. Contact',
          paragraph1:
            'If you have any questions about this Privacy Policy or how your data is handled, please contact us through the channels provided on the website.'
        }
      }
    },
    demo: {
      badge: 'Interactive demo',
      description: 'This is a sample of what you’d see after uploading your files and having at least one previous analysis to compare.',
      netChange: 'Net calculation: +5 new followers − 2 lost = +3 followers.',
      uploadTitle: 'Preparing the interactive demo',
      uploadDescription: 'We’re automatically generating the sample results. This should only take a moment.',
      cancel: 'Cancel demo'
    },
    upload: {
      title: 'Upload & analyze data',
      analyzing: 'Analyzing...',
      analyze: 'Analyze data',
      processing: 'Processing files...',
      unrecognizedFile: 'Unrecognized file. Please upload the Instagram ZIP.',
      secureBadge: '100% safe',
      removeFileGeneric: 'Remove file',
      processingFiles: 'Processing files...',
      readingZip: 'Reading ZIP...',
      validatingFiles: 'Validating files...',
      zipMissingData: 'We couldn’t find these files in the ZIP: {files}. Please request a new download in Instagram and try again.',
      dropHere: 'Drop the ZIP here',
      introTitle: 'Final step to generate your analysis',
      introDescription: 'Upload the ZIP file Instagram emailed you.',
      requiredTitle: 'Required ZIP',
      requiredHint: 'It’s the ZIP file Instagram emailed you.',
      statusUploaded: 'Uploaded',
      statusPartial: 'Incomplete',
      statusMissing: 'Missing',
      zipLabel: 'Instagram ZIP',
      zipDescription: '',
      instructionsHint: 'Haven’t downloaded the ZIP yet?',
      instructionsLink: 'View instructions',
      dropTitle: 'Drag the ZIP file Instagram emailed you here',
      dropDescription: 'Or tap to choose it from your device.',
      dropHint: 'One ZIP is enough.',
      selectFiles: 'Select ZIP'
    },
    instructions: {
      viewInstructions: 'View full instructions',
      wizardTitle: 'Get your data in 4 easy steps',
      wizardSubtitle: '',
      progressLabel: 'Step {current} of {total}',
      back: 'Back',
      next: 'Continue',
      goToUpload: 'Go to Upload files',
      jsonDefinition: 'Instagram provides a ZIP with your information. Upload it here and we’ll handle the rest.',
      privacyNote: 'Only you can see your files.',
      exportLink: 'https://accountscenter.instagram.com/info_and_permissions/dyi/',
      exportPopupBlocked: 'We could not open Instagram. Please allow pop-ups and try again.',
      steps: {
        overview: {
          title: 'What UnFollowed does',
          description: 'We turn the official Instagram export into clear, actionable insights.',
          point1: 'Spot who unfollowed you and access key stats quickly.',
          point2: 'Track new followers and compare periods in seconds.',
          point3: 'Everything happens in your browser—no installs, no add-ons.',
          reassurance: 'Your data, your control: delete it anytime.'
        },
        files: {
          title: 'Files required for the process',
          description: 'Instagram lets you download your data in a ZIP file.',
          jsonNote: 'That ZIP contains the information needed to analyze:',
          followersFile: '',
          followingFile: '',
          point1: 'Who follows you',
          point2: 'Who you follow',
          point3: 'Who doesn’t follow you back',
          point4: 'And many other details about your account',
          point5: 'All data is generated directly by Instagram.',
          point6: 'Secure, without sharing your password or putting your account at risk.'
        },
        download: {
          title: 'Request the ZIP export on Instagram',
          description: 'During the process, the password is entered on Instagram, not in our app.',
          importantTitle: '⚠️ IMPORTANT',
          importantLine1: 'Follow these steps carefully.',
          importantLine2: 'They’re key to processing your information correctly and generating the analysis without errors.',
          waitNote: '⏳ This process can take anywhere from a few minutes to about an hour, depending on how much data your account has.',
          preStepsNote: 'The following steps must be completed inside Instagram / Meta Accounts Center.',
          ctaTitle: 'Ready to get started?',
          ctaDescription: 'Click the button to copy the Instagram Accounts Center link, then open it in a new tab.',
          step1: 'Tap the “Create export” button.',
          step2: 'Choose your Instagram account in Accounts Center.',
          step3: 'Under “Information to download”, keep only “Connections” selected.',
          step4: 'Choose “All time”, pick the recommended format and “Media quality: Low”, then tap “Create files”.',
          tipConnections: 'Information: Connections (uncheck the rest).',
          tipFormat: 'Recommended format.',
          tipDateRange: 'Date range: All time.',
          tipQuality: 'Media quality: Low.',
          linkLabel: 'Copy link',
          linkCopiedLabel: 'Link copied'
        },
        after: {
          title: 'Come back to UnFollowed with the ZIP ready',
          description: '',
          point1: 'Download the ZIP from the email.',
          point2: 'Upload the ZIP and we’ll generate your analysis automatically.',
          point4: '',
          reminder: 'Repeat these steps whenever you want to update your metrics.'
        }
      }
    },
    loading: {
      title: 'Loading complete history',
      subtitle: 'Preparing all your previous analyses...',
      genericTitle: 'Loading...',
      genericSubtitle: 'Preparing your session...'
    },
    results: {
      title: 'Analysis Results',
      followers: 'Followers',
      following: 'Following',
      notReciprocal: 'Not following you back',
      newFollowers: 'New followers',
      downloadCSV: 'Download CSV',
      goBack: 'Go back',
      showHistorical: 'View History',
      search: 'Search users...',
      copyAll: 'Copy All',
      newAnalysis: 'Start New Analysis',
      backAction: 'Back',
      helpAction: 'FAQ / Help',
      confirmBackTitle: 'Go back?',
      confirmBackDescription: 'You’ll leave the current analysis. You can view it later from History.',
      confirmBackConfirm: 'Back',
      confirmBackCancel: 'Cancel',
      importantNoticeTitle: 'Important notices',
      importantNoticeBody:
        'Instagram may include deactivated accounts in the downloaded data. If you open a profile and see “user not found”, that account no longer exists and it isn’t a real unfollower.',
      importantNoticeLine1: 'Instagram may include deactivated accounts in the downloaded data.',
      importantNoticeLine2Prefix: 'If you open a profile and see',
      importantNoticeNotFound: 'user not found',
      importantNoticeLine2Middle: ', that account no longer exists and',
      importantNoticeNotUnfollower: 'it isn’t a real unfollower',
      importantNoticeLine2Suffix: '.',
      importantNoticeConfirm: 'Got it',
      notFollowingYouTitle: 'Not following you back',
      notFollowingBackTitle: 'Not following back',
      notFollowingYouShort: 'Not following',
      notFollowingBackShort: 'Not following back',
      notFollowingYouDescription: 'Users you follow but don\'t follow you back',
      notFollowingBackDescription: 'Users who follow you but you don\'t follow back',
      emptyNotFollowing: 'All users you follow also follow you back',
      emptyNotBack: 'You follow all users who follow you',
      downloadCsv: 'Download CSV'
    },
    comparison: {
      title: 'Comparison with Previous Analysis',
      followers: 'Followers',
      following: 'Following',
      change: 'Change',
      comparedWith: 'Compared with',
      lastAnalysis: 'Last analysis',
      unfollowed: 'Unfollowed you',
      newFollowers: 'New followers',
      youUnfollowed: 'You unfollowed',
      youFollowed: 'You followed'
    },
    historical: {
      title: 'Analysis History',
      count: 'Saved analyses: {count}',
      viewComplete: 'View Complete History',
      viewHistory: 'View History',
      viewAnalysis: 'View analysis',
      backToHistory: 'Back to history',
      noData: 'No saved analyses',
      noDataDescription: 'Once you perform your first analysis, data will appear here so you can compare changes over time.',
      expand: 'Expand',
      collapse: 'Collapse',
      backToMain: 'Back to Main',
      newUnfollowers: 'New unfollowers',
      newFollowers: 'New followers',
      newUnfollowersDetail: 'New users who don\'t follow you',
      newFollowersDetail: 'New users who now follow you',
      newFollowersFollowedDetail: 'New followers you follow',
      newFollowersNotFollowedDetail: 'New followers you don\'t follow',
      lostFollowersDetail: 'Users that unfollowed you',
      newFollowedDetail: 'Accounts you started following',
      showAll: 'Show all',
      showLess: 'Show less',
      andMore: 'and {count} more',
      selectAnalyses: 'Select analyses for CSV',
      downloadSelected: 'Download selected',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      filterFrom: 'From',
      filterTo: 'To',
      filterLabel: 'Filter',
      filteringLabel: 'Applying filter...',
      filteredRange: 'Filtered: {from} → {to}',
      applyFilters: 'Apply',
      selectAllShort: 'Sel. all',
      clearFilters: 'Clear filters',
      deleteSelected: 'Delete selected ({count})',
      bulkDeleteTitle: 'Delete {count} selected analyses',
      bulkDeleteDescription: 'This action cannot be undone.',
      bulkDeleteConfirm: 'Delete ({count})',
      clickToExpand: 'Click "Expand" to see detailed analysis',
      tapToExpand: 'Tap "Expand" to see detailed analysis'
    },
    newAnalysis: {
      allNewFollowers: 'All new followers',
      newFollowersYouFollow: 'New followers you follow',
      newFollowersNotFollowed: 'New followers you don\'t follow',
      lostFollowers: 'Lost followers',
      newFollowing: 'New following',
      allNotFollowingYou: 'All not following you back',
      allNotFollowingBack: 'All you don\'t follow back',
      changesSinceLastAnalysis: 'Changes since last analysis',
      currentAnalysis: 'Current analysis'
    },
    delete: {
      confirmTitle: 'Confirm Deletion',
      confirmDescription: 'Are you sure you want to delete this analysis from',
      confirm: 'Delete',
      deleting: 'Deleting...'
    },
    common: {
      all: 'All',
      success: 'Analysis completed successfully',
      successWithComparison: 'Analysis completed and compared with previous',
      firstAnalysisSaved: 'First analysis saved successfully',
      error: 'An error occurred during analysis',
      errorMissing: 'Required ZIP is missing',
      errorJson: 'Error reading data from the ZIP',
      errorProcessing: 'Error processing files',
      errorNoOriginalData: 'No original data available to download',
      originalFileDownloaded: 'Original file {filename} downloaded successfully',
      downloadOriginalFollowers: 'Download original followers file',
      downloadOriginalFollowing: 'Download original following file',
      errorLoadingHistory: 'Error loading historical analyses',
      errorLoginRequired: 'You must sign in to save the analysis',
      errorSavingAnalysis: 'Error saving the analysis',
      errorDeletingAnalysis: 'Error deleting analysis',
      login: 'Login',
      logout: 'Logout',
      back: 'Back',
      close: 'Close',
      confirm: 'Confirm',
      cancel: 'Cancel',
      yes: 'Yes',
      no: 'No',
      delete: 'Delete',
      search: 'Search users...',
      copyAll: 'Copy All',
      copy: 'Copy',
      copied: 'Copied',
      download: 'Download',
      downloadCsv: 'Download CSV',
      downloadJson: 'Download JSON',
      noResults: 'No results',
      dismiss: 'Dismiss',
      note: 'Note',
      tip: 'Tip',
      user: 'User'
    },
    auth: {
      signIn: 'Sign In',
      signUp: 'Sign Up',
      signOut: 'Sign Out',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm password',
      forgotPassword: 'Forgot your password?',
      noAccount: 'Don\'t have an account?',
      hasAccount: 'Already have an account?',
      loginSuccess: 'Welcome!',
      logoutSuccess: 'Successfully logged out',
      signUpSuccess: 'Account created successfully! Check your email to confirm.',
      error: 'Error signing in',
      errorInvalidCredentials: 'Incorrect email or password.',
      errorEmailNotConfirmed: 'Please confirm your account from the email before signing in.',
      errorSignUp: 'Error creating account',
      resetPasswordSent: 'We sent you a password reset link',
      resetPasswordSentDescription: "If an account exists for that email, we'll send you a link to reset your password.",
      resetPasswordError: 'We couldn\'t send the reset link',
      confirmationResent: 'We resent your confirmation email.',
      confirmationResendError: 'We couldn\'t resend the confirmation email.',
      confirmationCodeError: 'The code is invalid or expired. Please try again.',
      errorSignOut: 'Error signing out',
      errorUpdatePassword: 'Error updating password',
      passwordReuseError: 'Your new password must be different from the previous one.',
      passwordUpdated: 'Password updated successfully',
      deleteAccountSuccess: 'Your account has been deleted successfully.',
      errorDeleteAccount: 'We couldn\'t delete your account. Please try again.'
    },
    languages: {
      es: 'Español',
      en: 'English',
      pt: 'Português'
    },
    modal: {
      confirmAnalysis: 'Confirm Analysis',
      confirmTitle: 'Confirm Analysis',
      confirmDescription: 'Are you ready to process your Instagram files?',
      loginToSave: 'Log in to save and compare your analyses',
      continueWithoutLogin: 'Continue without logging in',
      analysisWillBeLost: 'Your analysis will not be saved and you won\'t be able to compare it with future analyses.',
      deleteConfirm: 'Are you sure you want to delete this analysis?',
      deleteWarning: 'This action cannot be undone.',
      deleteAnalysis: 'Delete analysis from',
      processNote: 'The process may take a few seconds depending on the size of your files.',
      loginTip: 'Log in to save your analysis and be able to compare it in the future.',
      login: 'Login',
      loginShort: 'Login',
      continue: 'Continue without account',
      continueShort: 'Continue',
      processAnalysis: 'Process Analysis'
    },
    csv: {
      notFollowingYou: 'Not following',
      notFollowingBack: 'Not following back',
      downloadSuccess: 'CSV downloaded with {count} users',
      analysisFrom: 'Analysis from',
      downloadModalTitle: 'CSV file download',
      downloadModalDescription:
        'Download the processed information from this analysis in CSV format. Choose one of the 3 options depending on what you want to review.',
      downloadModalNoteIntro:
        'This analysis can be downloaded in 3 CSV files (each one equals a tab):',
      downloadModalNoteItem1:
        'Followers and following: Includes the list of followers and following (columns: username, category).',
      downloadModalNoteItem2:
        'Not following and your fans: Accounts you follow that do not follow you + accounts that follow you and you do not follow (columns: username, category).',
      downloadModalNoteItem3:
        'Other info: Other lists available in your analysis (e.g. blocked). If there is no extra data, the CSV is empty with headers.',
      downloadModalNoteColumns:
        'Each CSV has 2 columns separated by comma: username and category.',
      downloadModalNoteEmpty:
        'If a category has no available data (for example, blocked users), it will not appear in the CSV.',
      downloadModalNoteSheets:
        'Open it in Excel or Google Sheets and filter by category to see the list you need.',
      downloadModalFilesTitle: 'Available files',
      downloadModalFile1Title: 'Followers and following',
      downloadModalFile1Subtitle: '(category = follower / following)',
      downloadModalFile2Title: 'Not following and your fans',
      downloadModalFile2Subtitle: '(category = not_following_you / your_fans)',
      downloadModalFile3Title: 'Other info',
      downloadModalFile3Subtitle: 'Other lists (e.g. blocked) if available'
    },
    loginSection: {
      optionalAccount: 'Optional account',
      whyCreate: 'Why create an account?',
      saveHistory: 'Save your analysis history',
      compareChanges: 'Compare follower changes',
      seeTrends: 'See trends and patterns',
      loginWithGoogle: 'Log in with Google',
      continueWithout: 'Continue without an account'
    },
    authModal: {
      subtitle: 'Sign in or create an account to save your history',
      continueWithGoogle: 'Continue with Google',
      or: 'or',
      signInTab: 'Sign In',
      signUpTab: 'Sign Up',
      resetTitle: 'Reset password',
      resetDescription: 'We’ll email you a link to create a new password.',
      resetSpamHint: 'If you don’t see it, check spam/promotions.',
      resetButton: 'Send link',
      resetCancel: 'Back to sign in',
      sendingReset: 'Sending...',
      resetConfirmTitle: 'Set a new password',
      resetConfirmDescription: 'Enter your new password to regain access.',
      resetConfirmButton: 'Save password',
      resetLinkExpired: 'The reset link has expired. Please request a new one.',
      resetRequestLink: 'Request new link',
      passwordMismatch: 'Passwords do not match.',
      emailPlaceholder: 'you@email.com',
      passwordPlaceholder: '••••••••',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Your name',
      signingIn: 'Signing in...',
      signingUp: 'Creating...',
      emailConfirmTitle: 'Check your inbox to confirm your account',
      emailConfirmDescription: 'We sent a 6-digit code to {email}.',
      emailConfirmHint: 'If you do not see it, check spam/promotions.',
      emailConfirmExpiry: 'The code expires in 1 hour.',
      emailConfirmCodeLabel: 'Verification code',
      emailConfirmCodePlaceholder: '000000',
      emailConfirmCodeButton: 'Confirm code',
      emailConfirmingCode: 'Verifying...',
      goToSignIn: 'Go to sign in',
      resendConfirmation: 'Resend confirmation email',
      resendingConfirmation: 'Resending...'
    },
    analysis: {
      detailedAnalysis: 'Detailed Analysis',
      downloadCompleteCSV: 'Download Complete CSV',
      changesSincePrevious: "What's changed",
      accountDataTitle: 'Account data',
      followersTotalLabel: 'Total followers',
      followingTotalLabel: 'Total following',
      currentAnalysis: 'Current analysis',
      noFollowYouShort: 'Not following',
      noFollowBackShort: 'Not following back',
      ratio: 'Ratio',
      loadingHistory: 'Loading history...',
      noPreviousAnalyses: 'No previous analyses',
      changeExplanation: 'Change Explanation: How did we get to {change} followers?',
      comparedWith: 'Compared with analysis from {date}',
      previousFollowers: 'Previous followers',
      newFollowersLabel: 'New followers',
      newFollowedLabel: 'New accounts followed',
      newFollowedDetail: 'New users you follow',
      lostFollowersLabel: 'Lost followers',
      currentFollowersLabel: 'Current followers',
      viewList: 'View list',
      newFollowersSection: 'New Followers',
      lostFollowersSection: 'Lost Followers',
      newFollowingSection: 'New Accounts You Follow',
      allNewFollowers: 'All',
      mutualFollowers: 'Mutual',
      notFollowingNew: 'Not following',
      allNewFollowersDesc: 'All new followers',
      newMutualFollowersDesc: 'New followers you also follow',
      newNotFollowingDesc: 'New followers you don\'t follow',
      usersWhoUnfollowed: 'Users who unfollowed you',
      noUnfollowers: 'Excellent! You haven\'t lost any followers',
      renamedFollowersSection: 'Users who changed their @',
      renamedFollowersList: 'Detected username changes',
      renamedFollowersTab: 'Renamed',
      noRenamedFollowers: 'No username changes detected',
      accountsYouStartedFollowing: 'Accounts you started following',
      noNewFollowing: 'You haven\'t followed new accounts',
      notFollowingYouTab: 'Not following you',
      notFollowingBackTab: 'Not following back',
      fansTitle: 'Your fans',
      dialogSubtitle: 'Compared with your previous analysis',
      dialogDescriptionNewFollowers: 'Users who started following you since your last analysis.',
      dialogDescriptionLostFollowers: 'Users who unfollowed you since your last analysis.',
      dialogDescriptionNotFollowingYou: 'Users you follow who don’t follow you back.',
      dialogDescriptionFans: 'Users who follow you but you don’t follow.',
      dialogDescriptionPendingRequests: 'Follow requests you sent that haven’t been accepted yet.',
      dialogDescriptionRequestsReceived: 'Users who requested to follow you and are still pending.',
      dialogDescriptionFavorites: 'Profiles you marked as favorites on Instagram.',
      dialogDescriptionBlocked: 'Profiles you blocked on Instagram.',
      dialogDescriptionCloseFriends: 'Users included in your Close Friends list.',
      dialogDescriptionHiddenStories: 'Profiles from whom you hid your stories.',
      renamedFrom: 'Previously: @{username}',
      netChangeLabel: 'Net change',
      dialogEmpty: 'No users in this category',
      emptyFirstTitle: 'This is your first analysis',
      emptyFirstDescription: 'There’s no previous analysis yet to compare and detect changes.',
      emptyLostTitle: 'Great news!',
      emptyLostDescription: 'You haven’t lost followers since your last analysis.',
      emptyNewTitle: 'No changes yet',
      emptyNewDescription: 'There were no new followers since your last analysis.',
      emptyGenericTitle: 'No users here',
      emptyGenericDescription: 'There are no users in this category.',
      openProfile: 'Open profile',
      notFollowingYouDescription: 'Users you follow but don\'t follow you back',
      notFollowingBackDescription: 'Users who follow you but you don\'t follow back',
      usersYouFollowButDontFollow: 'Users you follow but don\'t follow you',
      excellentAllFollow: 'Excellent! All users you follow also follow you back',
      usersWhoFollowYouButYouDont: 'Users who follow you but you don\'t follow',
      youFollowAllWhoFollow: 'You follow all users who follow you',
      usersWhoStartedFollowing: 'Users who started following you',
      noNewFollowersThisPeriod: 'No new followers in this period',
      andMoreUsers: 'And {count} more users...',
      statsSectionTitle: 'Recent stats',
      statsSectionDescription: 'See how followers and following evolve over time.',
      statsNoDataTitle: 'No stats yet',
      statsNoDataDescription: 'When you run your first analysis, you will be able to see follower trends and compare changes over time.',
      chartZoomHint: 'Use the slider below to zoom in or pan through the period.',
      followersEvolutionTitle: 'Followers evolution',
      newLostEvolutionTitle: 'New and lost followers over time',
      newLostEvolutionNoData: 'You need at least two analyses to see this trend.',
      timelineTitle: 'Activity timeline',
      timelineSubtitle: 'Activity detected from your saved analyses.',
      timelineTab: 'Recent activity',
      activityTitle: 'Recent activity',
      activityHelpAria: 'Recent activity help',
      activityHelpTitle: 'What do these events mean?',
      activityHelpMutual: 'Mutual: you follow each other.',
      activityHelpRename: 'Name change: the user changed their Instagram name.',
      activityHelpNote: 'This is shown separately to avoid confusion, since a name change can look like a new follower or a new following when it is actually the same account.',
      firstAnalysisInfoAria: 'First analysis information',
      firstAnalysisInfo: 'This is your first analysis. After your next analysis, a new tab with recent activity will be enabled.',
      activityFilters: {
        all: 'All',
        newFollowers: 'New followers',
        lostFollowers: 'Lost followers',
        newFollowed: 'New following',
        mutual: 'Mutual',
        renames: 'Name changes'
      },
      activityNewFollower: '@{username} started following you',
      activityLostFollower: '@{username} unfollowed you',
      activityNewFollowed: 'You started following @{username}',
      activityRenameLabel: 'Name change',
      mutualBadge: 'Mutual',
      activityFilterEmpty: 'No events for this filter in the last analysis.',
      activityEmptyTitle: 'No recent activity',
      activityEmptyDescription: 'No changes were detected since the last analysis.',
      moreInsightsTab: 'More insights',
      moreInsightsNote: '**Note**: these lists show the **total number of users** in each category, not only recent changes.',
      moreInsightsNoteFirst: '**Note**: these lists show the **total number of users** in each category.',
      moreInsightsDialogSubtitle: 'Full list (current state)',
      dialogComparedLine: 'Changes compared with the analysis from {date}.',
      dialogCurrentStateLine: 'This list shows the current state of your account.',
      dialogNoComparison: 'No previous analysis to compare.',
      moreInsights: {
        pendingRequests: 'Pending requests',
        requestsReceived: 'Requests received',
        favorites: 'Favorites',
        blocked: 'Blocked',
        closeFriends: 'Close friends',
        hiddenStories: 'Hidden stories'
      },
      viewMore: 'View more',
      timelineNeedsData: 'Add at least two analyses to unlock the timeline.',
      timelineNoChanges: 'No changes since your last analysis.',
      comparisonLabel: 'Compared with the last saved analysis from {date}',
      noPreviousAnalysis: 'No previous analysis',
      timelineNewFollower: 'New follower',
      timelineLostFollower: 'Unfollowed you',
      timelineRenamedFollower: 'Username change',
      timelineEmpty: 'No recent movements detected.',
      timelineRangeLabel: 'Show last analyses',
      timelineRangeLatest: 'Latest analysis',
      timelineRangeAll: 'All analyses',
      timelineRangeOption: 'Last {count} analyses',
      timelineSingleAnalysis: 'You only have one analysis from {date}. Run another to see timeline movements.'
    },
    instructionSteps: {
      step1Title: 'Open Instagram on your phone',
      step1Description: 'Go to your profile and tap the menu (☰) in the top right corner',
      step2Title: 'Settings and privacy',
      step2Description: 'Select \'Settings and privacy\' from the menu',
      step3Title: 'Accounts Center',
      step3Description: 'Tap \'Accounts Center\' and then \'Your information and permissions\'',
      step4Title: 'Download your information',
      step4Description: 'Select \'Download your information\' and then \'Download or transfer information\'',
      step5Title: 'Request download',
      step5Description: 'Choose \'Some of your information\' → Select \'Followers and following\' → Create files',
      step6Title: 'Wait for email',
      step6Description: 'Instagram will email you when it\'s ready (can take up to 48 hours)',
      step7Title: 'Upload the ZIP here',
      step7Description: 'Download the ZIP from the email and upload it to UnFollowed.',
      requiredFiles: 'Required ZIP',
      followersList: 'Your followers info',
      followingList: 'Your following info',
      tipMessage: 'The ZIP is enough; no need to extract it.'
    },
    userSettings: {
      accountSettings: 'Account Settings',
      manageAccount: 'Manage your account and security settings',
      profile: 'Profile',
      password: 'Password',
      danger: 'Danger',
      fullName: 'Full Name',
      email: 'Email',
      loginMethod: 'Login method',
      loginMethodEmail: 'Email & password',
      profileChangeNote: 'To change your personal information, contact support.',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      oauthPasswordMessage: 'This account was created using {provider}. Please update your password directly in your {provider} account.',
      updating: 'Updating...',
      updatePassword: 'Update password',
      dangerZone: 'Danger zone:',
      actionCannotBeUndone: 'This action cannot be undone.',
      deleteAccount: 'Delete account',
      confirmDeleteText: 'To confirm, type "DELETE" in the field below:',
      confirmDeletion: 'Confirm deletion',
      deleting: 'Deleting...',
      user: 'User'
    },
    feedback: {
      title: 'Share your feedback',
      subtitle: 'Let us know how we can improve, report an issue, or rate the app.',
      categoryLabel: 'What kind of feedback would you like to share?',
      categories: {
        issue: 'Problem or bug',
        idea: 'Idea or suggestion',
        other: 'Other'
      },
      ratingLabel: 'Overall rating',
      ratingHint: '1 = Very poor experience, 5 = Excellent',
      messageLabel: 'Your message',
      messagePlaceholder: 'Add details, links, or steps to reproduce the issue...',
      emailLabel: 'Contact email (optional)',
      emailPlaceholder: 'you@example.com',
      submit: 'Send feedback',
      submitting: 'Sending...',
      successTitle: 'Thanks for your feedback!',
      successDescription: 'Your message helps us improve UnFollowed.',
      errorTitle: 'We couldn’t send your feedback',
      errorDescription: 'Please try again in a few seconds.'
    },
    userList: {
      ofUsers: '{count} of {total} users',
      downloadedCSV: 'CSV downloaded with {count} users'
    },
    json: {
      downloadTitle: 'Download JSON Files',
      downloadDescription: 'Download the original JSON files you uploaded for this analysis. These are the raw data from Instagram.',
      analysisDateNote: 'Files from analysis performed on {date}',
      availableFiles: 'Available files',
      followersDescription: 'Complete list of your followers',
      followingDescription: 'Complete list of accounts you follow'
    },
    adminDashboard: {
      menuLink: 'Admin dashboard',
      badge: 'Admin only',
      title: 'Usage visibility dashboard',
      subtitle: 'Monitor how people use the product, where they connect from and how long sessions last. Data refreshes automatically from Supabase.',
      refresh: 'Refresh data',
      refreshing: 'Refreshing...',
      backToApp: 'Back to the app',
      loginRequired: 'Sign in to continue',
      loginHint: 'Only the owner account can open this dashboard.',
      loading: 'Loading live metrics...',
      noAccess: 'You do not have access',
      noAccessHint: 'Ask the owner to grant you admin access.',
      errorTitle: 'Metrics could not be loaded',
      errorDescription: 'Refresh the dashboard to try again.',
      retry: 'Try again',
      stats: {
        totalUsers: 'Total users',
        userHelper: 'Supabase profiles registered',
        activeSessions: 'Active sessions',
        activeHelper: 'Unique sessions (5 min window)',
        avgSession: 'Average duration',
        avgHelper: 'Based on the latest sessions',
        returningRate: 'Return rate',
        returningHelper: 'Users with 2+ sessions in 30 days',
        sessions30: 'Sessions (30 days)',
        sessionsHelper: 'Unique sessions recorded during the last month',
        uniqueLogins30: 'Unique logins (30 days)',
        uniqueLogins30Helper: 'Users who logged in at least once',
        newUsers: 'New users',
        newUsersHelper: 'Created in the last 30 days',
        loginEvents: 'Recent logins (last 14 days)'
      },
      signupHealth: {
        title: 'Signup health (30 days)',
        subtitle: 'Activation funnel from signup to first analysis.',
        signups: 'Signups',
        verified: 'Verified',
        firstSession: 'First session',
        firstAnalysis: 'First analysis',
        avgSignupToVerified: 'Avg signup -> verified',
        avgVerifiedToSession: 'Avg verified -> first session',
        avgSessionToAnalysis: 'Avg first session -> first analysis'
      },
      details: {
        user: 'User',
        country: 'Country',
        lastAccess: 'Last access',
        sessionsCount: 'Sessions',
        firstAccess: 'First access',
        browser: 'Browser',
        device: 'Device',
        os: 'Operating system',
        mobile: 'Mobile OS',
        firstSession: 'First session',
        lastSession: 'Last session',
        noAccess: 'No records',
        waitingVerification: 'Waiting for verification',
        empty: 'No users to display.',
        pageInfo: 'Showing {from}-{to} of {total}',
        rowsPerPage: 'Rows per page',
        previousPage: 'Previous',
        nextPage: 'Next',
        countryFilter: 'Country',
        providerFilter: 'Provider',
        allCountries: 'All countries',
        allProviders: 'All providers',
        all: 'All',
        from: 'From',
        to: 'To',
        filtersTitle: 'Filters',
        filtersSubtitle: 'Apply global filters to analyze the dashboard main metrics.',
        firstAccessFrom: 'First access from',
        firstAccessTo: 'First access to',
        lastAccessFrom: 'Last access from',
        lastAccessTo: 'Last access to',
        firstAccessModalDescription: 'Select a range to filter by first access date.',
        lastAccessModalDescription: 'Select a range to filter by last access date.',
        clearFilters: 'Clear filters',
        actions: 'Actions',
        copyUserId: 'Copy user id',
        copyEmail: 'Copy email',
        openSupabase: 'Open in Supabase',
        filterByCountry: 'Filter by country',
        filterByProvider: 'Filter by provider',
        scrollableTable: 'Scrollable user detail table',
        totalUsersDescription: 'List of registered users.',
        activeSessionsDescription: 'Users with active sessions in the last 5 minutes.',
        newUsersDescription: 'Users created in the last 30 days.',
        returningRateDescription: 'Users with 2+ sessions in the last 30 days.',
        sessions30Description: 'Users with sessions recorded in the last 30 days.',
        uniqueLogins30Description: 'Users who logged in at least once in the last 30 days.',
        countriesDescription: 'Users grouped by country with their latest connection.',
        browsersDescription: 'Users grouped by browser with their latest connection.'
      },
      charts: {
        logins: 'Logins',
        sessions: 'Sessions',
        loginTrend: 'Login trend (14 days)',
        loginTrendHelper: 'Events captured during the last two weeks',
        loginTrendNote: 'Includes every login event (not unique)',
        sessionDuration: 'Session duration distribution',
        sessionsByHour: 'Sessions by hour',
        devices: 'Devices',
        os: 'Operating systems',
        mobileOs: 'Mobile (iOS / Android)',
        countries: 'Top countries',
        browsers: 'Browsers',
        analysisLeaderboard: 'Analyses performed',
        topUsers: 'Most active users',
        sessions30Note: 'Based on session count (last 30 days).',
        uniqueSessions30Title: 'Unique sessions (30 days)',
        uniqueSessions30Note: 'Counts unique users per category.',
        allSessionsTitle: 'All sessions (all time)',
        allSessionsNote: 'Based on all recorded sessions.',
        sessionsLabel: 'sessions',
        lastLogin: 'Last login: {date}',
        noLoginRecorded: 'No data recorded',
        countryUnknown: 'Unknown country',
        analysisCountLabel: 'analyses'
      },
      emptyStates: {
        noLogins: 'Not enough login events yet.',
        noCountries: 'Country information is still empty.',
        noSessions: 'No sessions available for this chart.',
        noDevices: 'Device information is not available yet.',
        noOs: 'OS information is not available yet.',
        noMobileOs: 'Not enough mobile sessions yet.',
        noBrowsers: 'Browser information is not available yet.',
        noLoginUsers: 'No user login events yet.',
        noUsers: 'No highlighted users yet.',
        noAnalysisUsers: 'No analyses performed yet.'
      },
      feedback: {
        title: 'Latest feedback',
        subtitle: 'Recent comments, ratings and reports from users.',
        empty: 'No feedback entries yet.',
        anonymous: 'Anonymous user',
        rating: 'Rating: {rating}/5',
        contactLabel: 'Contact',
        categoryLabel: 'Category',
        sortLabel: 'Sort rating',
        sortAsc: 'Lowest to highest',
        sortDesc: 'Highest to lowest'
      }
    },
    sidebar: {
      navigation: 'Navigation',
      instructions: 'Instructions',
      instructionsDesc: 'How to use the app',
      instructionsShort: 'Instructions',
      upload: 'Upload Files',
      uploadDesc: 'Upload and analyze your data',
      uploadShort: 'Upload',
      history: 'History',
      historyDesc: 'View previous analyses',
      historyShort: 'History',
      stats: 'Insights',
      statsDesc: 'View charts and timeline',
      statsShort: 'Insights',
      blog: 'Blog',
      blogDesc: 'Articles and updates',
      analysis: 'Detailed Analysis',
      analysisDesc: 'View detailed analysis',
      settings: 'Customizations',
      language: 'Language',
      theme: 'Theme',
      feedback: 'Leave feedback',
      feedbackDesc: 'Report issues or share ideas'
    },
    mobileHome: {
      title: 'Welcome',
      titleName: 'Welcome, {name}',
      titleBack: 'Welcome back',
      titleBackName: 'Welcome back, {name}',
      subtitle: 'Ready to explore your Instagram insights?',
      cta: 'Lets get started',
      home: 'Home',
      homeDesc: 'Welcome screen'
    }
  },
  pt: {
    header: {
      title: 'UnFollowed',
      subtitle: 'Analise seus seguidores e quem você segue',
      login: 'Entrar',
      logout: 'Sair',
      settings: 'Configurações',
      theme: {
        light: 'Claro',
        dark: 'Escuro',
        system: 'Sistema'
      }
    },
    welcome: {
      title: 'Analise seus seguidores do Instagram',
      subtitle: 'Descubra quem não te segue de volta e quem te segue',
      description: 'Envie seus dados do Instagram para obter uma análise detalhada dos seus seguidores e de quem você segue.',
      followSteps: 'Siga estes passos para obter seus dados do Instagram'
    },
      landing: {
      hero: {
        title: 'Descubra com segurança quem deixou de seguir você no Instagram',
        titleLeading: 'Descubra com segurança quem',
        titleAccent: 'deixou de seguir você no Instagram',
        subtitle: 'Transforme os dados dos seus seguidores do Instagram em insights acionáveis em minutos.',
        primaryCta: 'Entrar para começar',
        howItWorksCta: 'Como funciona',
        securityChip: 'Você nunca digita sua senha do Instagram no nosso app; tudo acontece no site oficial do Instagram.'
      },
      seo: {
        title: 'UnFollowed | Veja quem deixou de seguir você no Instagram',
        description: 'O UnFollowed ajuda você a ver quem deixou de seguir você no Instagram com uma análise segura baseada no ZIP oficial do Instagram.',
        keywords: 'UnFollowed, Instagram, seguidores, quem deixou de seguir, unfollowers, analise de seguidores'
      },
      steps: {
        title: 'Como funciona'
      },
      howItWorks: {
        demoCta: 'Ver demo interativa',
        downloadButton: 'Baixar dados',
        downloadingLabel: 'Baixando...',
        downloadComplete: 'Download concluído',
        downloadAgain: 'Baixar novamente',
        dropLabel: 'Solte seu arquivo aqui',
        uploadUploaded: 'Arquivo enviado ✓',
        uploadProcessing: 'Processando...',
        uploadReady: 'Pronto',
        play: 'Reproduzir',
        pause: 'Pausar',
        steps: {
          download: {
            title: 'Baixe seus dados',
            subtitle: '',
            description: 'Solicite a exportação dos seus dados no Instagram para acessar suas informações.'
          },
          upload: {
            title: 'Envie seus dados',
            subtitle: '',
            description: 'Arraste e solte o arquivo ZIP que você baixou do Instagram para analisar.'
          },
          results: {
            title: 'Veja resultados na hora',
            subtitle: '',
            description: 'Identifique quem deixou de te seguir no Instagram e veja suas métricas de forma simples.'
          }
        },
        resultsTabs: {
          notFollowing: 'Você não segue',
          notFollowed: 'Não te seguem'
        },
        uploadSuccess: 'Arquivos enviados com sucesso',
        actions: {
          copyUser: 'Copiar usuário',
          openProfile: 'Ver perfil'
        }
      },
      trust: {
        title: 'Privacidade em primeiro lugar',
        privacy: 'Seus arquivos nunca saem do seu dispositivo',
        privacyDescription: 'Só você pode ver seus arquivos e apagá-los a qualquer momento.',
        highlight1: 'Criptografia ponta a ponta',
        highlight2: 'Compatibilidade garantida com o Instagram'
      },
      features: {
        title: 'Principais funcionalidades',
        cards: {
          notFollowingBack: {
            title: 'Usuários perdidos',
            description: 'Lista completa de perfis que você segue mas não te seguem'
          },
          newFollowers: {
            title: 'Novos seguidores',
            description: 'Descubra quem começou a te seguir recentemente'
          },
          stats: {
            title: 'Estatísticas detalhadas',
            description: 'Gráficos e métricas para acompanhar seu crescimento'
          },
          timeline: {
            title: 'Linha do tempo',
            description: 'Veja como seus seguidores mudam ao longo do tempo'
          }
        }
      },
      faq: {
        title: 'Perguntas frequentes',
        items: {
          safeUnfollowed: {
            question: '🔍 Como ver quem deixou de me seguir no Instagram de graça e com segurança?',
            answer:
              'Com o UnFollowed, você pode ver quem deixou de te seguir no Instagram de forma gratuita e segura.\nBasta baixar suas informações do Instagram em um arquivo ZIP nas configurações da sua conta e enviar para o app. O UnFollowed analisa seus seguidores e seguidos para mostrar quem deixou de te seguir — sem fazer login no Instagram, sem compartilhar sua senha e sem usar aplicativos pagos.'
          },
          accountSafety: {
            question: '🛡️ Usar este app pode fazer o Instagram bloquear ou suspender minha conta?',
            answer:
              'Não. Este app é 100% seguro e não viola as regras do Instagram.\nDiferente de outros apps semelhantes:\n• Não pedimos seu usuário ou senha\n• Não acessamos sua conta diretamente\n• Não fazemos scraping\n• Não executamos ações automáticas\nA análise é feita apenas com os dados oficiais que o Instagram permite baixar, mantendo sua conta totalmente segura contra bloqueios ou suspensões.'
          },
          accuracy: {
            question: '🎯 Quão precisa é a análise?',
            answer:
              'A análise é muito precisa porque se baseia nos dados oficiais exportados pelo Instagram.\nQuanto mais vezes você enviar arquivos atualizados, mais informações terá sobre suas métricas ao longo do tempo, incluindo mudanças em relação à última análise.'
          },
          autoTracking: {
            question: '🤖 O app faz acompanhamento automático?',
            answer:
              'Não, e isso é intencional. Para manter sua conta segura e evitar qualquer risco, o app não se conecta automaticamente ao Instagram.\nSempre que quiser uma análise atualizada, basta enviar o novo arquivo ZIP que o Instagram fornece.'
          },
          whyDownload: {
            question: '📦 Por que preciso baixar o arquivo pelo Instagram?',
            answer:
              'O Instagram não permite que apps externos acessem diretamente suas informações privadas.\nBaixar o arquivo ZIP é a forma oficial e segura de obter seus dados e manter total controle da sua conta.'
          },
          generationTime: {
            question: '⏱️ Quanto tempo o Instagram leva para gerar o arquivo?',
            answer:
              'Normalmente de alguns minutos até cerca de uma hora, dependendo da quantidade de informações da sua conta.\nQuando estiver pronto, o Instagram avisa por email.'
          },
          techKnowledge: {
            question: '🙋‍♀️ Preciso de conhecimento técnico para usar o app?',
            answer:
              'Não. É super fácil: baixe o ZIP do Instagram, faça upload e pronto.\nNós cuidamos do resto.'
          },
          unfollowNotifications: {
            question: '🔔 O Instagram avisa quando alguém deixa de te seguir?',
            answer:
              'Não. O Instagram não envia notificações quando alguém deixa de te seguir.\nPor isso, o UnFollowed é uma forma simples e segura de saber quem deixou de te seguir sem precisar comparar manualmente seguidores e seguidos.'
          },
          blockedUsers: {
            question: '🚫 O UnFollowed pode mostrar quem te bloqueou no Instagram?',
            answer:
              'O UnFollowed foi desenvolvido para detectar quem deixou de te seguir a partir dos dados oficiais que você baixa do Instagram.\nSe alguém te bloqueia, o Instagram deixa de mostrar o perfil, as publicações e os stories dessa pessoa. No entanto, o Instagram não inclui informações de bloqueio nos dados baixáveis, então nenhum app (incluindo o UnFollowed) consegue mostrar uma lista exata de quem te bloqueou.\nO que o UnFollowed faz é identificar mudanças nos seguidores e apontar quem deixou de te seguir, o que em alguns casos pode coincidir com um bloqueio.'
          }
        }
      },
      benefits: {
        items: {
          secure: {
            title: '100% seguro',
            subtitle: 'Seus dados, seu controle.'
          },
          unfollowed: {
            title: 'Descubra quem deixou de seguir você',
            subtitle: 'Identifique de forma simples e clara quem deixou de te seguir.'
          },
          noAccess: {
            title: 'Não requer acesso ao Instagram',
            subtitle: 'Você nunca compartilha sua senha com este app.'
          },
          free: {
            title: '100% grátis',
            subtitle: 'Use todas as funcionalidades sem custo.'
          }
        }
      },
      cta: {
        title: 'Pronto para descobrir seus insights?',
        description: 'Comece agora e receba análises detalhadas dos seus seguidores em minutos.',
        button: 'Começar agora'
      },
      footer: {
        copyright: '© 2026 UnFollowed. Todos os direitos reservados.',
        disclaimer: 'Não temos afiliação com o Instagram ou a Meta.'
      },
      footerLinks: {
        navigation: 'Navegação',
        home: 'Início',
        howItWorks: 'Como funciona',
        faq: 'FAQ',
        legal: 'Legal',
        terms: 'Termos e condições',
        privacy: 'Política de privacidade',
        contactTitle: 'Ficou com dúvidas?',
        contactCta: 'Fale conosco'
      }
    },
    termsPage: {
      title: 'Termos e condições do UnFollowed',
      intro: 'Leia estes Termos e condições atentamente antes de usar nosso serviço.',
      effectiveDate: 'Data de vigência: 01/01/2026',
      sections: {
        introduction: {
          title: '1. Introdução',
          paragraph1:
            'O UnFollowed oferece uma ferramenta que permite analisar seguidores e pessoas que deixaram de seguir no Instagram usando apenas os dados oficiais que o usuário baixa do Instagram.',
          paragraph2: 'Ao acessar ou usar o UnFollowed, você concorda com estes Termos e condições.'
        },
        use: {
          title: '2. Uso do serviço',
          paragraph1:
            'O UnFollowed permite enviar arquivos de dados do Instagram obtidos diretamente do Instagram para identificar unfollowers e mudanças de seguidores ao longo do tempo.',
          bullets: {
            noPassword: 'Não solicitamos seu usuário nem sua senha do Instagram.',
            noDirectAccess: 'Não acessamos contas do Instagram diretamente.',
            noAutomation: 'Não realizamos scraping, automações ou ações em nome dos usuários.',
            userProvided: 'Toda a análise é feita exclusivamente com os dados fornecidos voluntariamente pelo usuário.'
          }
        },
        privacy: {
          title: '3. Privacidade',
          paragraph1: 'Respeitamos sua privacidade e estamos comprometidos em proteger seus dados.',
          paragraph2:
            'Consulte nossa Política de privacidade para detalhes sobre como coletamos, processamos, armazenamos e protegemos suas informações.'
        },
        responsible: {
          title: '4. Uso responsável',
          paragraph1:
            'Os usuários concordam em usar o UnFollowed de forma responsável e em conformidade com todas as leis e regulamentos aplicáveis.',
          paragraph2:
            'Você não pode usar o serviço para fins ilegais, prejudiciais, abusivos, difamatórios ou invasivos, incluindo assédio ou violação da privacidade de terceiros.',
          paragraph3:
            'O UnFollowed não se responsabiliza por como os usuários interpretam ou utilizam as informações fornecidas pelo serviço. Os usuários são responsáveis por suas ações e consequências.'
        },
        limitations: {
          title: '5. Limitações do serviço',
          paragraph1:
            'O UnFollowed fornece insights com base nos dados disponibilizados pelo Instagram por meio de suas ferramentas de exportação.',
          bullets: {
            noGuarantees:
              'Não garantimos precisão absoluta, integridade ou disponibilidade ininterrupta do serviço.',
            resultsMayVary: 'Os resultados podem variar conforme a qualidade, a completude e o momento dos dados enviados.',
            instagramChanges:
              'Mudanças na estrutura de dados ou nas políticas de exportação do Instagram podem afetar a funcionalidade ou a precisão do serviço.'
          },
          paragraph2: 'Os usuários reconhecem e aceitam essas limitações.'
        },
        changes: {
          title: '6. Alterações nos termos',
          paragraph1: 'Reservamo-nos o direito de atualizar ou modificar estes Termos e condições a qualquer momento.',
          paragraph2: 'Qualquer alteração será publicada nesta página e terá efeito imediato após a publicação.',
          paragraph3:
            'Ao continuar usando o UnFollowed após as mudanças, você concorda com os termos atualizados.'
        }
      }
    },
    privacyPage: {
      title: 'Política de Privacidade do UnFollowed',
      intro:
        'Sua privacidade é importante para nós. Esta Política de Privacidade explica como o UnFollowed coleta, usa e protege suas informações.',
      effectiveDate: 'Data de vigência: 01/01/2026',
      sections: {
        introduction: {
          title: '1. Introdução',
          paragraph1:
            'O UnFollowed permite analisar dados de seguidores do Instagram a partir de arquivos baixados do Instagram e enviados voluntariamente pelo usuário.',
          paragraph2:
            'Ao usar o UnFollowed, você concorda com a coleta e o uso das informações conforme esta Política de Privacidade.'
        },
        collect: {
          title: '2. Informações que coletamos',
          paragraph1: 'Coletamos apenas os dados mínimos necessários para oferecer o serviço.',
          subheadingProvided: 'a) Informações que você fornece',
          bullets: {
            instagramFiles:
              'Arquivos de dados do Instagram que você baixa do Instagram (como o ZIP de exportação).',
            technicalMetadata:
              'Metadados técnicos necessários para processar a análise (por exemplo, estrutura de arquivos ou marcações de tempo).'
          },
          subheadingNotRequested: 'Não solicitamos:',
          notBullets: {
            noUsername: 'Seu usuário ou senha do Instagram.',
            noDirectAccess: 'Acesso direto à sua conta do Instagram.'
          }
        },
        use: {
          title: '3. Como usamos suas informações',
          paragraph1: 'As informações fornecidas são usadas exclusivamente para:',
          bullets: {
            analyze: 'Analisar seguidores e quem deixou de seguir.',
            generate: 'Gerar insights e visualizações dentro do app.',
            improve: 'Melhorar o desempenho e a confiabilidade do serviço.'
          },
          paragraph2: 'Não usamos seus dados para publicidade, perfilagem ou revenda.'
        },
        processing: {
          title: '4. Processamento e armazenamento de dados',
          paragraph1: 'Todos os dados são processados apenas para entregar a análise solicitada.',
          paragraph2: 'Os arquivos enviados não são compartilhados com terceiros.',
          paragraph3: 'Você pode excluir seus dados enviados a qualquer momento.',
          paragraph4:
            'Adotamos medidas técnicas e organizacionais razoáveis para proteger suas informações contra acesso não autorizado, perda ou uso indevido.'
        },
        thirdParty: {
          title: '5. Serviços de terceiros',
          paragraph1: 'O UnFollowed não se integra à API do Instagram e não realiza scraping ou automação.',
          paragraph2:
            'Podemos usar provedores de infraestrutura confiáveis (como hospedagem ou ferramentas de analytics) apenas para operar e melhorar o serviço. Esses provedores são obrigados a manter confidencialidade e proteção de dados.'
        },
        cookies: {
          title: '6. Cookies e analytics',
          paragraph1:
            'Podemos usar analytics básicos e cookies para entender como os usuários interagem com o app (por exemplo, visualizações de página e duração de sessão).',
          bullets: {
            anonymized: 'Os dados são anonimizados.',
            noFiles: 'Não incluem os arquivos do Instagram enviados.',
            improveExperience: 'São usados apenas para melhorar a experiência e o desempenho.'
          },
          paragraph2: 'Você pode desativar cookies nas configurações do seu navegador.'
        },
        retention: {
          title: '7. Retenção de dados',
          paragraph1: 'Mantemos os dados apenas pelo tempo necessário para prestar o serviço ou até que o usuário os exclua.',
          paragraph2:
            'Após a exclusão, os dados são removidos permanentemente dos nossos sistemas em um prazo razoável.'
        },
        rights: {
          title: '8. Seus direitos',
          paragraph1: 'Dependendo da sua jurisdição, você pode ter o direito de:',
          bullets: {
            access: 'Acessar seus dados.',
            delete: 'Solicitar a exclusão dos seus dados.',
            restrict: 'Restringir ou se opor ao processamento.'
          },
          paragraph2: 'Você pode exercer esses direitos entrando em contato conosco.'
        },
        changes: {
          title: '9. Alterações nesta Política de Privacidade',
          paragraph1: 'Podemos atualizar esta Política de Privacidade periodicamente.',
          paragraph2: 'Qualquer alteração será publicada nesta página e terá efeito imediato após a publicação.',
          paragraph3:
            'Ao continuar usando o UnFollowed após as mudanças, você concorda com a política atualizada.'
        },
        contact: {
          title: '10. Contato',
          paragraph1:
            'Se você tiver dúvidas sobre esta Política de Privacidade ou sobre como seus dados são tratados, entre em contato pelos canais informados no site.'
        }
      }
    },
    demo: {
      badge: 'Demo interativa',
      description: 'Este é um exemplo do que aparece ao enviar seus arquivos e já ter pelo menos uma análise anterior para comparar.',
      netChange: 'Cálculo líquido: +5 seguidores novos − 2 perdidos = +3 seguidores.',
      uploadTitle: 'Gerando a demo interativa',
      uploadDescription: 'Estamos preparando automaticamente os resultados de exemplo. Isso leva apenas alguns segundos.',
      cancel: 'Cancelar demo'
    },
    upload: {
      title: 'Enviar e analisar dados',
      analyzing: 'Analisando...',
      analyze: 'Analisar dados',
      processing: 'Processando análises...',
      unrecognizedFile: 'Arquivo não reconhecido. Envie o ZIP baixado do Instagram.',
      secureBadge: '100% seguro',
      removeFileGeneric: 'Remover arquivo',
      processingFiles: 'Processando arquivos...',
      readingZip: 'Lendo ZIP...',
      validatingFiles: 'Validando arquivos...',
      zipMissingData: 'Não encontramos estes arquivos no ZIP: {files}. Solicite um novo download no Instagram e tente novamente.',
      dropHere: 'Solte o ZIP aqui',
      introTitle: 'Última etapa para gerar sua análise',
      introDescription: 'Envie o arquivo ZIP que o Instagram enviou por email.',
      requiredTitle: 'ZIP necessário',
      requiredHint: 'É o ZIP que o Instagram enviou por email.',
      statusUploaded: 'Enviado',
      statusPartial: 'Incompleto',
      statusMissing: 'Falta',
      zipLabel: 'ZIP do Instagram',
      zipDescription: '',
      instructionsHint: 'Ainda não baixou o ZIP?',
      instructionsLink: 'Ver instruções',
      dropTitle: 'Arraste o arquivo ZIP que o Instagram enviou por email aqui',
      dropDescription: 'Ou toque para escolhê-lo no seu dispositivo.',
      dropHint: 'Um único ZIP é suficiente.',
      selectFiles: 'Selecionar ZIP'
    },
    instructions: {
      viewInstructions: 'Ver instruções completas',
      wizardTitle: 'Prepare seus dados em 4 passos',
      wizardSubtitle: '',
      progressLabel: 'Passo {current} de {total}',
      back: 'Voltar',
      next: 'Continuar',
      goToUpload: 'Ir para enviar arquivos',
      jsonDefinition: 'O Instagram gera um ZIP com suas informações. Envie-o aqui e cuidamos do resto.',
      privacyNote: 'Só você pode ver seus arquivos.',
      exportLink: 'https://accountscenter.instagram.com/info_and_permissions/dyi/',
      exportPopupBlocked: 'Não foi possível abrir o Instagram. Permita pop-ups e tente novamente.',
      steps: {
        overview: {
          title: 'O que o UnFollowed faz',
          description: 'Transformamos a exportação oficial do Instagram em insights claros e acionáveis.',
          point1: 'Descubra quem deixou de te seguir e acesse estatísticas chave.',
          point2: 'Acompanhe novos seguidores e compare períodos em segundos.',
          point3: 'Tudo acontece no navegador, sem instalar nada.',
          reassurance: 'Seus dados, seu controle: você pode apagar quando quiser.'
        },
        files: {
          title: 'Arquivos necessários para o processo',
          description: 'O Instagram permite baixar seus dados em um arquivo ZIP.',
          jsonNote: 'Esse ZIP contém as informações necessárias para analisar:',
          followersFile: '',
          followingFile: '',
          point1: 'Quem te segue',
          point2: 'Quem você segue',
          point3: 'Quem não te segue de volta',
          point4: 'E muitos outros dados relacionados à sua conta',
          point5: 'Todos os dados são gerados diretamente pelo Instagram.',
          point6: 'Seguro e sem compartilhar sua senha ou colocar sua conta em risco.'
        },
        download: {
          title: 'Solicite a exportação do ZIP no Instagram',
          description: 'Durante o processo, a senha é digitada no Instagram, não no nosso app.',
          importantTitle: '⚠️ IMPORTANTE',
          importantLine1: 'Siga estes passos com atenção.',
          importantLine2: 'Eles são essenciais para processarmos todas as informações corretamente e gerar a análise sem erros.',
          waitNote: '⏳ Este processo pode levar de alguns minutos até cerca de uma hora, dependendo da quantidade de informações da sua conta.',
          preStepsNote: 'Os passos abaixo devem ser feitos dentro do Instagram / Centro de contas da Meta.',
          ctaTitle: 'Pronto para começar?',
          ctaDescription:
            'Clique no botão para copiar o link do Centro de Contas do Instagram e depois abra-o em uma nova aba.',
          step1: 'Toque no botão “Create export”.',
          step2: 'Escolha sua conta do Instagram no Centro de Contas.',
          step3: 'Em “Informações para baixar”, mantenha apenas “Connections” marcado.',
          step4: 'Selecione “All time”, escolha o formato recomendado e “Media quality: Low”, e confirme em “Create files”.',
          tipConnections: 'Informação: Connections (desmarque o restante).',
          tipFormat: 'Formato recomendado.',
          tipDateRange: 'Período: All time.',
          tipQuality: 'Media quality: Low.',
          linkLabel: 'Copiar link',
          linkCopiedLabel: 'Link copiado'
        },
        after: {
          title: 'Volte ao UnFollowed com o ZIP pronto',
          description: '',
          point1: 'Baixe o ZIP do email.',
          point2: 'Envie o ZIP e geramos sua análise automaticamente.',
          point4: '',
          reminder: 'Repita esses passos sempre que quiser atualizar suas métricas.'
        }
      }
    },
    loading: {
      title: 'Carregando histórico completo',
      subtitle: 'Preparando todas as suas análises anteriores...',
      genericTitle: 'Carregando...',
      genericSubtitle: 'Preparando sua sessão...'
    },
    results: {
      title: 'Resultados da Análise',
      followers: 'Seguidores',
      following: 'Seguindo',
      notReciprocal: 'Não te seguem de volta',
      newFollowers: 'Novos seguidores',
      downloadCSV: 'Baixar CSV',
      goBack: 'Voltar',
      showHistorical: 'Ver Histórico',
      search: 'Buscar usuários...',
      copyAll: 'Copiar Tudo',
      newAnalysis: 'Realizar Nova Análise',
      backAction: 'Voltar',
      helpAction: 'FAQ / Ajuda',
      confirmBackTitle: 'Voltar?',
      confirmBackDescription: 'Você vai sair da análise atual. Você poderá vê-la depois no histórico.',
      confirmBackConfirm: 'Voltar',
      confirmBackCancel: 'Cancelar',
      importantNoticeTitle: 'Avisos importantes',
      importantNoticeBody:
        'O Instagram pode incluir contas desativadas nos dados baixados. Se ao abrir um perfil você vir “user not found”, essa conta não existe mais e não se trata de um unfollower real.',
      importantNoticeLine1: 'O Instagram pode incluir contas desativadas nos dados baixados.',
      importantNoticeLine2Prefix: 'Se ao abrir um perfil você vir',
      importantNoticeNotFound: 'user not found',
      importantNoticeLine2Middle: ', essa conta não existe mais e',
      importantNoticeNotUnfollower: 'não se trata de um unfollower real',
      importantNoticeLine2Suffix: '.',
      importantNoticeConfirm: 'Entendido',
      notFollowingYouTitle: 'Não te seguem de volta',
      notFollowingBackTitle: 'Não seguindo de volta',
      notFollowingYouShort: 'Não seguem',
      notFollowingBackShort: 'Não segue de volta',
      notFollowingYouDescription: 'Usuários que você segue mas não te seguem de volta',
      notFollowingBackDescription: 'Usuários que te seguem mas você não segue de volta',
      emptyNotFollowing: 'Todos os usuários que você segue também te seguem de volta',
      emptyNotBack: 'Você segue todos os usuários que te seguem',
      downloadCsv: 'Baixar CSV'
    },
    comparison: {
      title: 'Comparação com Análise Anterior',
      followers: 'Seguidores',
      following: 'Seguindo',
      change: 'Mudança',
      comparedWith: 'Comparado com',
      lastAnalysis: 'Última análise',
      unfollowed: 'Pararam de seguir',
      newFollowers: 'Novos seguidores',
      youUnfollowed: 'Você parou de seguir',
      youFollowed: 'Você começou a seguir'
    },
    historical: {
      title: 'Histórico de Análises',
      count: 'Análises salvas: {count}',
      viewComplete: 'Ver Histórico Completo',
      viewHistory: 'Ver Histórico',
      viewAnalysis: 'Ver análise',
      backToHistory: 'Voltar ao histórico',
      noData: 'Nenhuma análise salva',
      noDataDescription: 'Uma vez que você realizar sua primeira análise, os dados aparecerão aqui para que você possa comparar mudanças ao longo do tempo.',
      expand: 'Expandir',
      collapse: 'Recolher',
      backToMain: 'Voltar ao Início',
      newUnfollowers: 'Novos que pararam de seguir',
      newFollowers: 'Novos seguidores',
      newUnfollowersDetail: 'Novos usuários que não te seguem',
      newFollowersDetail: 'Novos usuários que agora te seguem',
      newFollowersFollowedDetail: 'Novos seguidores que você segue',
      newFollowersNotFollowedDetail: 'Novos seguidores que você não segue',
      lostFollowersDetail: 'Usuários que deixaram de seguir você',
      newFollowedDetail: 'Contas que você começou a seguir',
      showAll: 'Mostrar tudo',
      showLess: 'Mostrar menos',
      andMore: 'e mais {count}',
      selectAnalyses: 'Selecionar análises para CSV',
      downloadSelected: 'Baixar selecionados',
      selectAll: 'Seleccionar tudo',
      deselectAll: 'Desmarcar tudo',
      filterFrom: 'De',
      filterTo: 'Até',
      filterLabel: 'Filtro',
      filteringLabel: 'Aplicando filtro...',
      filteredRange: 'Filtrado: {from} → {to}',
      applyFilters: 'Aplicar',
      selectAllShort: 'Sel. tudo',
      clearFilters: 'Limpar filtros',
      deleteSelected: 'Excluir selecionados ({count})',
      bulkDeleteTitle: 'Excluir {count} análises selecionadas',
      bulkDeleteDescription: 'Esta ação não pode ser desfeita.',
      bulkDeleteConfirm: 'Excluir ({count})',
      clickToExpand: 'Clique em "Expandir" para ver análise detalhada',
      tapToExpand: 'Toque em "Expandir" para ver análise detalhada'
    },
    newAnalysis: {
      allNewFollowers: 'Todos os novos seguidores',
      newFollowersYouFollow: 'Novos seguidores que você segue',
      newFollowersNotFollowed: 'Novos seguidores que você não segue',
      lostFollowers: 'Seguidores perdidos',
      newFollowing: 'Novos que você segue',
      allNotFollowingYou: 'Todos que não te seguem de volta',
      allNotFollowingBack: 'Todos que você não segue de volta',
      changesSinceLastAnalysis: 'Mudanças desde a última análise',
      currentAnalysis: 'Análise atual'
    },
    delete: {
      confirmTitle: 'Confirmar Exclusão',
      confirmDescription: 'Tem certeza de que deseja excluir esta análise de',
      confirm: 'Excluir',
      deleting: 'Excluindo...'
    },
    common: {
      all: 'Todos',
      success: 'Análise concluída com sucesso',
      successWithComparison: 'Análise concluída e comparada com a anterior',
      firstAnalysisSaved: 'Primeira análise salva com sucesso',
      error: 'Ocorreu um erro durante a análise',
      errorMissing: 'Falta o ZIP necessário',
              errorJson: 'Erro ao ler os dados do ZIP',
      errorProcessing: 'Erro ao processar arquivos',
      errorNoOriginalData: 'Nenhum dado original disponível para download',
      originalFileDownloaded: 'Arquivo original {filename} baixado com sucesso',
      downloadOriginalFollowers: 'Baixar arquivo original de seguidores',
      downloadOriginalFollowing: 'Baixar arquivo original de seguindo',
      errorLoadingHistory: 'Erro ao carregar análises históricas',
      errorLoginRequired: 'É necessário entrar para salvar a análise',
      errorSavingAnalysis: 'Erro ao salvar a análise',
      errorDeletingAnalysis: 'Erro ao excluir análise',
      login: 'Entrar',
      logout: 'Sair',
      back: 'Voltar',
      close: 'Fechar',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
      yes: 'Sim',
      no: 'Não',
      delete: 'Excluir',
      search: 'Buscar usuários...',
      copyAll: 'Copiar Tudo',
      copy: 'Copiar',
      copied: 'Copiado',
      download: 'Baixar',
      downloadCsv: 'Baixar CSV',
      downloadJson: 'Baixar JSON',
      noResults: 'Sem resultados',
      dismiss: 'Dispensar',
      note: 'Nota',
      tip: 'Dica',
      user: 'Usuário'
    },
    auth: {
      signIn: 'Entrar',
      signUp: 'Cadastrar',
      signOut: 'Sair',
      email: 'Email',
      password: 'Senha',
      confirmPassword: 'Confirmar senha',
      forgotPassword: 'Esqueceu sua senha?',
      noAccount: 'Não tem uma conta?',
      hasAccount: 'Já tem uma conta?',
      loginSuccess: 'Bem-vindo!',
      logoutSuccess: 'Logout realizado com sucesso',
      signUpSuccess: 'Conta criada com sucesso! Verifique seu email para confirmar.',
      error: 'Erro ao entrar',
      errorInvalidCredentials: 'Email ou senha incorretos.',
      errorEmailNotConfirmed: 'Confirme sua conta no email antes de entrar.',
      errorSignUp: 'Erro ao criar conta',
      resetPasswordSent: 'Enviamos um link para redefinir sua senha',
      resetPasswordSentDescription: 'Se existir uma conta com esse email, vamos enviar um link para redefinir sua senha.',
      resetPasswordError: 'Não foi possível enviar o link de redefinição',
      confirmationResent: 'Reenviamos o email de confirmação.',
      confirmationResendError: 'Não foi possível reenviar o email de confirmação.',
      confirmationCodeError: 'O código é inválido ou expirou. Tente novamente.',
      errorSignOut: 'Erro ao sair',
      errorUpdatePassword: 'Erro ao atualizar a senha',
      passwordReuseError: 'A nova senha precisa ser diferente da anterior.',
      passwordUpdated: 'Senha atualizada com sucesso',
      deleteAccountSuccess: 'Sua conta foi excluída permanentemente.',
      errorDeleteAccount: 'Não foi possível excluir sua conta. Tente novamente.'
    },
    languages: {
      es: 'Español',
      en: 'English',
      pt: 'Português'
    },
    modal: {
      confirmAnalysis: 'Confirmar Análise',
      confirmTitle: 'Confirmar Análise',
      confirmDescription: 'Está pronto para processar seus arquivos do Instagram?',
      loginToSave: 'Faça login para salvar e comparar suas análises',
      continueWithoutLogin: 'Continuar sem fazer login',
      analysisWillBeLost: 'Sua análise não será salva e você não poderá compará-la com análises futuras.',
      deleteConfirm: 'Tem certeza de que deseja excluir esta análise?',
      deleteWarning: 'Esta ação não pode ser desfeita.',
      deleteAnalysis: 'Excluir análise de',
      processNote: 'O processo pode levar alguns segundos dependendo do tamanho dos seus arquivos.',
      loginTip: 'Faça login para salvar sua análise e poder compará-la no futuro.',
      login: 'Entrar',
      loginShort: 'Entrar',
      continue: 'Continuar sem conta',
      continueShort: 'Continuar',
      processAnalysis: 'Processar Análise'
    },
    csv: {
      notFollowingYou: 'Não segue',
      notFollowingBack: 'Não segue de volta',
      downloadSuccess: 'CSV baixado com {count} usuários',
      analysisFrom: 'Análise de',
      downloadModalTitle: 'Download de arquivos CSV',
      downloadModalDescription:
        'Baixe as informações processadas desta análise em formato CSV. Escolha uma das 3 opções conforme o que deseja revisar.',
      downloadModalNoteIntro:
        'Esta análise pode ser baixada em 3 arquivos CSV (cada um equivale a uma aba):',
      downloadModalNoteItem1:
        'Followers and following: Lista de seguidores e seguindo (colunas: username, category).',
      downloadModalNoteItem2:
        'Not following and your fans: Contas que você segue e não seguem você + contas que seguem você e você não segue (colunas: username, category).',
      downloadModalNoteItem3:
        'Other info: Outras listas disponíveis na análise (ex: blocked). Se não houver dados extras, o CSV fica vazio com headers.',
      downloadModalNoteColumns:
        'Cada CSV tem 2 colunas separadas por vírgula: username e category.',
      downloadModalNoteEmpty:
        'Se uma categoria não tiver dados disponíveis (por exemplo, blocked users), ela não aparecerá no CSV.',
      downloadModalNoteSheets:
        'Abra no Excel ou Google Sheets e filtre por category para ver a lista desejada.',
      downloadModalFilesTitle: 'Arquivos disponíveis',
      downloadModalFile1Title: 'Followers and following',
      downloadModalFile1Subtitle: '(category = follower / following)',
      downloadModalFile2Title: 'Not following and your fans',
      downloadModalFile2Subtitle: '(category = not_following_you / your_fans)',
      downloadModalFile3Title: 'Other info',
      downloadModalFile3Subtitle: 'Other lists (e.g. blocked) if available'
    },
    loginSection: {
      optionalAccount: 'Conta opcional',
      whyCreate: 'Por que criar uma conta?',
      saveHistory: 'Salve seu histórico de análises',
      compareChanges: 'Compare mudanças nos seguidores',
      seeTrends: 'Veja tendências e padrões',
      loginWithGoogle: 'Entrar com Google',
      continueWithout: 'Continuar sem conta'
    },
    authModal: {
      subtitle: 'Faça login ou crie uma conta para salvar seu histórico',
      continueWithGoogle: 'Continuar com Google',
      or: 'ou',
      signInTab: 'Entrar',
      signUpTab: 'Cadastrar',
      resetTitle: 'Redefinir senha',
      resetDescription: 'Enviaremos um link por email para criar uma nova senha.',
      resetSpamHint: 'Se não encontrar, verifique spam/promoções.',
      resetButton: 'Enviar link',
      resetCancel: 'Voltar ao login',
      sendingReset: 'Enviando...',
      resetConfirmTitle: 'Definir nova senha',
      resetConfirmDescription: 'Digite sua nova senha para recuperar o acesso.',
      resetConfirmButton: 'Salvar senha',
      resetLinkExpired: 'O link para redefinir a senha expirou. Solicite um novo.',
      resetRequestLink: 'Solicitar novo link',
      passwordMismatch: 'As senhas não coincidem.',
      emailPlaceholder: 'voce@email.com',
      passwordPlaceholder: '••••••••',
      fullName: 'Nome completo',
      fullNamePlaceholder: 'Seu nome',
      signingIn: 'Entrando...',
      signingUp: 'Criando...',
      emailConfirmTitle: 'Verifique seu email para confirmar a conta',
      emailConfirmDescription: 'Enviamos um código de 6 dígitos para {email}.',
      emailConfirmHint: 'Se não encontrar, verifique spam/promoções.',
      emailConfirmExpiry: 'O código expira em 1 hora.',
      emailConfirmCodeLabel: 'Código de verificação',
      emailConfirmCodePlaceholder: '000000',
      emailConfirmCodeButton: 'Confirmar código',
      emailConfirmingCode: 'Verificando...',
      goToSignIn: 'Ir para login',
      resendConfirmation: 'Reenviar email de confirmação',
      resendingConfirmation: 'Reenviando...'
    },
    analysis: {
      detailedAnalysis: 'Análise Detalhada',
      downloadCompleteCSV: 'Baixar CSV Completo',
      changesSincePrevious: 'Resumo de mudanças',
      accountDataTitle: 'Dados da sua conta',
      followersTotalLabel: 'Total de seguidores',
      followingTotalLabel: 'Total de seguidos',
      currentAnalysis: 'Análise atual',
      noFollowYouShort: 'Não seguem',
      noFollowBackShort: 'Não segue de volta',
      ratio: 'Proporção',
      loadingHistory: 'Carregando histórico...',
      noPreviousAnalyses: 'Nenhuma análise anterior',
      changeExplanation: 'Explicação da Mudança: Como chegamos a {change} seguidores?',
      comparedWith: 'Comparado com análise de {date}',
      previousFollowers: 'Seguidores anteriores',
      newFollowersLabel: 'Novos seguidores',
      newFollowedLabel: 'Novas contas seguidas',
      newFollowedDetail: 'Novos usuários que você segue',
      lostFollowersLabel: 'Seguidores perdidos',
      currentFollowersLabel: 'Seguidores atuais',
      viewList: 'Ver lista',
      newFollowersSection: 'Novos Seguidores',
      lostFollowersSection: 'Seguidores Perdidos',
      newFollowingSection: 'Novas Contas que Você Segue',
      allNewFollowers: 'Todos',
      mutualFollowers: 'Mútuos',
      notFollowingNew: 'Não segue',
      allNewFollowersDesc: 'Todos os novos seguidores',
      newMutualFollowersDesc: 'Novos seguidores que você também segue',
      newNotFollowingDesc: 'Novos seguidores que você não segue',
      usersWhoUnfollowed: 'Usuários que pararam de seguir você',
      noUnfollowers: 'Excelente! Você não perdeu nenhum seguidor',
      renamedFollowersSection: 'Usuários que mudaram o @',
      renamedFollowersList: 'Mudanças de @ detectadas',
      renamedFollowersTab: 'Renomeados',
      noRenamedFollowers: 'Nenhuma mudança de @ detectada',
      accountsYouStartedFollowing: 'Contas que você começou a seguir',
      noNewFollowing: 'Você não seguiu novas contas',
      notFollowingYouTab: 'Não te seguem',
      notFollowingBackTab: 'Não segue de volta',
      fansTitle: 'Seus fãs',
      dialogSubtitle: 'Comparado com sua análise anterior',
      dialogDescriptionNewFollowers: 'Pessoas que começaram a seguir você desde a última análise.',
      dialogDescriptionLostFollowers: 'Pessoas que deixaram de seguir você desde a última análise.',
      dialogDescriptionNotFollowingYou: 'Pessoas que você segue, mas que não seguem você de volta.',
      dialogDescriptionFans: 'Pessoas que seguem você, mas que você não segue.',
      dialogDescriptionPendingRequests: 'Solicitações de seguimento que você enviou e ainda não foram aceitas.',
      dialogDescriptionRequestsReceived: 'Pessoas que pediram para seguir você e ainda estão pendentes.',
      dialogDescriptionFavorites: 'Perfis que você marcou como favoritos no Instagram.',
      dialogDescriptionBlocked: 'Perfis que você bloqueou no Instagram.',
      dialogDescriptionCloseFriends: 'Pessoas incluídas na sua lista de Close Friends.',
      dialogDescriptionHiddenStories: 'Perfis de quem você ocultou seus stories.',
      renamedFrom: 'Antes: @{username}',
      netChangeLabel: 'Mudança líquida',
      dialogEmpty: 'Nenhum usuário nesta categoria',
      emptyFirstTitle: 'Esta é sua primeira análise',
      emptyFirstDescription: 'Ainda não há uma análise anterior para comparar e detectar mudanças.',
      emptyLostTitle: 'Ótimo!',
      emptyLostDescription: 'Você não perdeu seguidores desde a última análise.',
      emptyNewTitle: 'Sem mudanças por enquanto',
      emptyNewDescription: 'Não houve novos seguidores desde a última análise.',
      emptyGenericTitle: 'Nenhum usuário aqui',
      emptyGenericDescription: 'Não há usuários nesta categoria.',
      openProfile: 'Abrir perfil',
      notFollowingYouDescription: 'Usuários que você segue mas não te seguem de volta',
      notFollowingBackDescription: 'Usuários que te seguem mas você não segue de volta',
      usersYouFollowButDontFollow: 'Usuários que você segue mas não te seguem',
      excellentAllFollow: 'Excelente! Todos os usuários que você segue também te seguem de volta',
      usersWhoFollowYouButYouDont: 'Usuários que te seguem mas você não segue',
      youFollowAllWhoFollow: 'Você segue todos os usuários que te seguem',
      usersWhoStartedFollowing: 'Usuários que começaram a seguir você',
      noNewFollowersThisPeriod: 'Nenhum novo seguidor neste período',
      andMoreUsers: 'E mais {count} usuários...',
      statsSectionTitle: 'Estatísticas recentes',
      statsSectionDescription: 'Veja como seguidores e seguindo evoluem ao longo do tempo.',
      statsNoDataTitle: 'Ainda não há estatísticas',
      statsNoDataDescription: 'Quando você fizer sua primeira análise, poderá ver a evolução dos seguidores e comparar mudanças ao longo do tempo.',
      chartZoomHint: 'Use o controle deslizante abaixo para aplicar zoom ou navegar pelo período.',
      followersEvolutionTitle: 'Evolução de seguidores',
      newLostEvolutionTitle: 'Evolução de novos seguidores e perdas',
      newLostEvolutionNoData: 'Você precisa de pelo menos duas análises para ver essa evolução.',
      timelineTitle: 'Linha do tempo de atividade',
      timelineSubtitle: 'Movimentos detectados a partir das análises realizadas.',
      timelineTab: 'Atividade recente',
      activityTitle: 'Atividade recente',
      activityHelpAria: 'Ajuda da atividade recente',
      activityHelpTitle: 'O que esses eventos significam?',
      activityHelpMutual: 'Mútuo: vocês se seguem.',
      activityHelpRename: 'Mudança de nome: o usuário mudou o nome no Instagram.',
      activityHelpNote: 'Isso é mostrado separadamente para evitar confusão, pois uma mudança de nome pode parecer um novo seguidor ou um novo seguido, quando na verdade é a mesma conta.',
      firstAnalysisInfoAria: 'Informação da primeira análise',
      firstAnalysisInfo: 'Esta é a sua primeira análise. Na próxima análise, uma nova aba com a atividade recente será habilitada.',
      activityFilters: {
        all: 'Todos',
        newFollowers: 'Novos seguidores',
        lostFollowers: 'Seguidores perdidos',
        newFollowed: 'Novos seguidos',
        mutual: 'Mútuo',
        renames: 'Mudanças de nome'
      },
      activityNewFollower: '@{username} começou a te seguir',
      activityLostFollower: '@{username} deixou de te seguir',
      activityNewFollowed: 'Você começou a seguir @{username}',
      activityRenameLabel: 'Mudança de nome',
      mutualBadge: 'Mútuo',
      activityFilterEmpty: 'Não há eventos para este filtro na última análise.',
      activityEmptyTitle: 'Sem atividade recente',
      activityEmptyDescription: 'Nenhuma mudança foi detectada desde a última análise.',
      moreInsightsTab: 'Mais informações',
      moreInsightsNote: '**Nota**: estas listas mostram a **quantidade total de usuários** em cada categoria, não apenas mudanças recentes.',
      moreInsightsNoteFirst: '**Nota**: estas listas mostram a **quantidade total de usuários** em cada categoria.',
      moreInsightsDialogSubtitle: 'Lista completa (estado atual)',
      dialogComparedLine: 'Mudanças comparadas com a análise de {date}.',
      dialogCurrentStateLine: 'Esta lista mostra o estado atual da sua conta.',
      dialogNoComparison: 'Não há análise anterior para comparar.',
      moreInsights: {
        pendingRequests: 'Solicitações pendentes',
        requestsReceived: 'Solicitações recebidas',
        favorites: 'Favoritos',
        blocked: 'Bloqueados',
        closeFriends: 'Melhores amigos',
        hiddenStories: 'Histórias ocultas'
      },
      viewMore: 'Ver mais',
      timelineNeedsData: 'Adicione pelo menos duas análises para ver a linha do tempo.',
      timelineNoChanges: 'Nenhuma mudança desde a última análise.',
      comparisonLabel: 'Comparado com a última análise salva de {date}',
      noPreviousAnalysis: 'Sem análise anterior',
      timelineNewFollower: 'Novo seguidor',
      timelineLostFollower: 'Deixou de te seguir',
      timelineRenamedFollower: 'Mudança de nome',
      timelineEmpty: 'Nenhum movimento recente detectado.',
      timelineRangeLabel: 'Mostrar últimas análises',
      timelineRangeLatest: 'Última análise',
      timelineRangeAll: 'Todas',
      timelineRangeOption: '{count} análises',
      timelineSingleAnalysis: 'Você tem apenas uma análise de {date}. Faça outra para ver movimentos.'
    },
    instructionSteps: {
      step1Title: 'Abra o Instagram no seu celular',
      step1Description: 'Vá para seu perfil e toque no menu (☰) no canto superior direito',
      step2Title: 'Configurações e privacidade',
      step2Description: 'Selecione \'Configurações e privacidade\' no menu',
      step3Title: 'Central de contas',
      step3Description: 'Toque em \'Central de contas\' e depois \'Suas informações e permissões\'',
      step4Title: 'Baixar suas informações',
      step4Description: 'Selecione \'Baixar suas informações\' e depois \'Baixar ou transferir informações\'',
      step5Title: 'Solicitar download',
      step5Description: 'Escolha \'Algumas de suas informações\' → Selecione \'Followers and following\' → Criar arquivos',
      step6Title: 'Aguardar o email',
      step6Description: 'O Instagram enviará um email quando estiver pronto (pode levar até 48 horas)',
      step7Title: 'Envie o ZIP aqui',
      step7Description: 'Baixe o ZIP do email e envie no UnFollowed.',
      requiredFiles: 'ZIP necessário',
      followersList: 'Suas informações de seguidores',
      followingList: 'Suas informações de seguindo',
      tipMessage: 'O ZIP é suficiente; não é necessário extraí-lo.'
    },
    userSettings: {
      accountSettings: 'Configurações da Conta',
      manageAccount: 'Gerencie sua conta e configurações de segurança',
      profile: 'Perfil',
      password: 'Senha',
      danger: 'Perigo',
      fullName: 'Nome completo',
      email: 'Email',
      loginMethod: 'Método de login',
      loginMethodEmail: 'Email e senha',
      profileChangeNote: 'Para alterar suas informações pessoais, entre em contato com o suporte.',
      newPassword: 'Nova senha',
      confirmPassword: 'Confirmar senha',
      oauthPasswordMessage: 'Esta conta foi criada usando {provider}. Atualize sua senha diretamente na sua conta {provider}.',
      updating: 'Atualizando...',
      updatePassword: 'Atualizar senha',
      dangerZone: 'Zona de perigo:',
      actionCannotBeUndone: 'Esta ação não pode ser desfeita.',
      deleteAccount: 'Excluir conta',
      confirmDeleteText: 'Para confirmar, digite "EXCLUIR" no campo abaixo:',
      confirmDeletion: 'Confirmar exclusão',
      deleting: 'Excluindo...',
      user: 'Usuário'
    },
    feedback: {
      title: 'Envie seu feedback',
      subtitle: 'Conte como podemos melhorar, reporte um problema ou avalie o app.',
      categoryLabel: 'Qual tipo de feedback você quer enviar?',
      categories: {
        issue: 'Problemas',
        idea: 'Ideia ou sugestão',
        other: 'Outros'
      },
      ratingLabel: 'Avaliação geral',
      ratingHint: '1 = Experiência ruim, 5 = Excelente',
      messageLabel: 'Sua mensagem',
      messagePlaceholder: 'Adicione detalhes, links ou passos para reproduzir o problema...',
      emailLabel: 'Email para contato (opcional)',
      emailPlaceholder: 'voce@email.com',
      submit: 'Enviar feedback',
      submitting: 'Enviando...',
      successTitle: 'Obrigado pelo feedback!',
      successDescription: 'Sua mensagem nos ajuda a melhorar o UnFollowed.',
      errorTitle: 'Não conseguimos enviar seu feedback',
      errorDescription: 'Tente novamente em alguns segundos.'
    },
    userList: {
      ofUsers: '{count} de {total} usuários',
      downloadedCSV: 'CSV baixado com {count} usuários'
    },
    json: {
      downloadTitle: 'Baixar Arquivos JSON',
      downloadDescription: 'Baixe os arquivos JSON originais que você enviou para esta análise. Estes são os dados brutos do Instagram.',
      analysisDateNote: 'Arquivos da análise realizada em {date}',
      availableFiles: 'Arquivos disponíveis',
      followersDescription: 'Lista completa dos seus seguidores',
      followingDescription: 'Lista completa das contas que você segue'
    },
    adminDashboard: {
      menuLink: 'Painel admin',
      badge: 'Somente admin',
      title: 'Painel de visibilidade de uso',
      subtitle: 'Acompanhe como os usuários usam o produto, de onde acessam e quanto tempo ficam. Os dados são atualizados automaticamente do Supabase.',
      refresh: 'Atualizar dados',
      refreshing: 'Atualizando...',
      backToApp: 'Voltar ao app',
      loginRequired: 'Entre para continuar',
      loginHint: 'Somente a conta administradora pode abrir este painel.',
      loading: 'Carregando métricas em tempo real...',
      noAccess: 'Você não tem acesso',
      noAccessHint: 'Peça ao proprietário para liberar seu usuário como admin.',
      errorTitle: 'Não foi possível carregar as métricas',
      errorDescription: 'Atualize o painel para tentar novamente.',
      retry: 'Tentar novamente',
      stats: {
        totalUsers: 'Usuários totais',
        userHelper: 'Perfis registrados no Supabase',
        activeSessions: 'Sessões ativas',
        activeHelper: 'Sessões únicas (janela de 5 min)',
        avgSession: 'Duração média',
        avgHelper: 'Baseado nas últimas sessões',
        returningRate: 'Taxa de retorno',
        returningHelper: 'Usuários com 2+ sessões em 30 dias',
        sessions30: 'Sessões (30 dias)',
        sessionsHelper: 'Sessões únicas registradas no último mês',
        uniqueLogins30: 'Usuários únicos (30 dias)',
        uniqueLogins30Helper: 'Usuários que fizeram login ao menos uma vez',
        newUsers: 'Novos usuários',
        newUsersHelper: 'Criados nos últimos 30 dias',
        loginEvents: 'Logins recentes (últimos 14 dias)'
      },
      signupHealth: {
        title: 'Saúde do signup (30 dias)',
        subtitle: 'Funil de ativação do cadastro até a primeira análise.',
        signups: 'Signups',
        verified: 'Verificados',
        firstSession: 'Primeira sessão',
        firstAnalysis: 'Primeira análise',
        avgSignupToVerified: 'Média signup -> verificado',
        avgVerifiedToSession: 'Média verificado -> primeira sessão',
        avgSessionToAnalysis: 'Média primeira sessão -> primeira análise'
      },
      details: {
        user: 'Usuário',
        country: 'País',
        lastAccess: 'Último acesso',
        sessionsCount: 'Sessões',
        firstAccess: 'Primeiro acesso',
        browser: 'Navegador',
        device: 'Dispositivo',
        os: 'Sistema operacional',
        mobile: 'OS mobile',
        firstSession: 'Primeira sessão',
        lastSession: 'Última sessão',
        noAccess: 'Sem registros',
        waitingVerification: 'Aguardando verificação',
        empty: 'Não há usuários para mostrar.',
        pageInfo: 'Mostrando {from}-{to} de {total}',
        rowsPerPage: 'Linhas por página',
        previousPage: 'Anterior',
        nextPage: 'Próxima',
        countryFilter: 'País',
        providerFilter: 'Provedor',
        allCountries: 'Todos os países',
        allProviders: 'Todos os provedores',
        all: 'Todos',
        from: 'De',
        to: 'Até',
        filtersTitle: 'Filtros',
        filtersSubtitle: 'Aplique filtros globais para analisar as principais métricas do painel.',
        firstAccessFrom: 'Primeiro acesso de',
        firstAccessTo: 'Primeiro acesso até',
        lastAccessFrom: 'Último acesso de',
        lastAccessTo: 'Último acesso até',
        firstAccessModalDescription: 'Selecione um intervalo para filtrar pela data do primeiro acesso.',
        lastAccessModalDescription: 'Selecione um intervalo para filtrar pela data do último acesso.',
        clearFilters: 'Limpar filtros',
        actions: 'Ações',
        copyUserId: 'Copiar user id',
        copyEmail: 'Copiar email',
        openSupabase: 'Abrir no Supabase',
        filterByCountry: 'Filtrar por país',
        filterByProvider: 'Filtrar por provedor',
        scrollableTable: 'Tabela rolável de detalhes de usuários',
        totalUsersDescription: 'Lista de usuários registrados.',
        activeSessionsDescription: 'Usuários com sessões ativas nos últimos 5 minutos.',
        newUsersDescription: 'Usuários criados nos últimos 30 dias.',
        returningRateDescription: 'Usuários com 2+ sessões nos últimos 30 dias.',
        sessions30Description: 'Usuários com sessões registradas nos últimos 30 dias.',
        uniqueLogins30Description: 'Usuários que fizeram login ao menos uma vez nos últimos 30 dias.',
        countriesDescription: 'Usuários agrupados por país com a última conexão.',
        browsersDescription: 'Usuários agrupados por navegador com a última conexão.'
      },
      charts: {
        logins: 'Logins',
        sessions: 'Sessões',
        loginTrend: 'Tendência de logins (14 dias)',
        loginTrendHelper: 'Eventos capturados nas últimas duas semanas',
        loginTrendNote: 'Inclui todos os eventos de login (não únicos)',
        sessionDuration: 'Distribuição da duração',
        sessionsByHour: 'Sessões por hora',
        devices: 'Dispositivos',
        os: 'Sistemas operacionais',
        mobileOs: 'Mobile (iOS / Android)',
        countries: 'Principais países',
        browsers: 'Navegadores',
        analysisLeaderboard: 'Análises realizadas',
        topUsers: 'Usuários mais ativos',
        sessions30Note: 'Baseado na quantidade de sessões (últimos 30 dias).',
        uniqueSessions30Title: 'Sessões únicas (30 dias)',
        uniqueSessions30Note: 'Conta usuários únicos por categoria.',
        allSessionsTitle: 'Todas as sessões (histórico)',
        allSessionsNote: 'Baseado em todas as sessões registradas.',
        sessionsLabel: 'sessões',
        lastLogin: 'Último login: {date}',
        noLoginRecorded: 'Sem dados',
        countryUnknown: 'País desconhecido',
        analysisCountLabel: 'análises'
      },
      emptyStates: {
        noLogins: 'Ainda não há eventos suficientes.',
        noCountries: 'Ainda sem dados de países.',
        noSessions: 'Sem sessões disponíveis.',
        noDevices: 'Ainda sem informações de dispositivos.',
        noOs: 'Ainda sem informações de sistemas operacionais.',
        noMobileOs: 'Sem dados móveis suficientes.',
        noBrowsers: 'Ainda sem dados de navegadores.',
        noLoginUsers: 'Ainda não há eventos de login por usuário.',
        noUsers: 'Nenhum usuário em destaque ainda.',
        noAnalysisUsers: 'Ainda não há análises realizadas.'
      },
      feedback: {
        title: 'Feedback dos usuários',
        subtitle: 'Mensagens, notas e problemas reportados recentemente.',
        empty: 'Ainda não recebemos feedback.',
        anonymous: 'Usuário anônimo',
        rating: 'Avaliação: {rating}/5',
        contactLabel: 'Contato',
        categoryLabel: 'Categoria',
        sortLabel: 'Ordenar',
        sortAsc: 'Menor para maior',
        sortDesc: 'Maior para menor'
      }
    },
    sidebar: {
      navigation: 'Navegação',
      instructions: 'Instruções',
      instructionsDesc: 'Como usar o app',
      instructionsShort: 'Instruções',
      upload: 'Carregar Arquivos',
      uploadDesc: 'Envie e analise seus dados',
      uploadShort: 'Enviar',
      history: 'Histórico',
      historyDesc: 'Ver análises anteriores',
      historyShort: 'Histórico',
      stats: 'Estatísticas',
      statsDesc: 'Ver gráficos e timeline',
      statsShort: 'Estatísticas',
      blog: 'Blog',
      blogDesc: 'Artigos e novidades',
      analysis: 'Análise Detalhada',
      analysisDesc: 'Ver análise detalhada',
      settings: 'Customizações',
      language: 'Idioma',
      theme: 'Tema',
      feedback: 'Enviar feedback',
      feedbackDesc: 'Reporte problemas ou compartilhe ideias'
    },
    mobileHome: {
      title: 'Bem-vindo',
      titleName: 'Bem-vindo, {name}',
      titleBack: 'Bem-vindo de volta',
      titleBackName: 'Bem-vindo de volta, {name}',
      subtitle: 'Pronto para explorar seus insights do Instagram?',
      cta: 'Vamos começar',
      home: 'Início',
      homeDesc: 'Tela de boas-vindas'
    }
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const serverLanguage = getServerLanguage();
    if (serverLanguage) {
      return serverLanguage;
    }
    const pathLanguage = getPathLanguage();
    if (pathLanguage) {
      return pathLanguage;
    }
    return 'en';
  });

  const setLanguage = useCallback((lang: Language) => {
    if (lang === language) {
      return;
    }
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem(languageStorageKey, lang);
      const nextPath = replaceLangInPath(window.location.pathname, lang);
      const nextUrl = `${nextPath}${window.location.search}${window.location.hash}`;
      window.history.replaceState({}, document.title, nextUrl);
    }
    trackEvent('language_changed', { language: lang });
  }, [language]);
  
  useEffect(() => {
    setAnalyticsLanguage(language);
  }, [language]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleLanguageChange = () => {
      const pathLanguage = getPathLanguage();
      if (pathLanguage) {
        setLanguageState(pathLanguage);
        return;
      }
      setLanguageState('en');
    };

    window.addEventListener('languagechange', handleLanguageChange);
    return () => window.removeEventListener('languagechange', handleLanguageChange);
  }, []);

  const t = (key: string, params?: Record<string, unknown>): string => {
    const keys = key.split('.');
    let value: unknown = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        console.warn(`Translation key "${key}" not found for language "${language}"`);
        return key;
      }
    }
    
    if (typeof value === 'string') {
      return interpolate(value, params);
    }
    
    console.warn(`Translation key "${key}" is not a string for language "${language}"`);
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
